import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { base_url } from "../utils/baseUrl";
import { config } from "../utils/axiosconfig";
import { useDispatch, useSelector } from "react-redux";
import { uploadCsvs, csvDownloads } from "../features/payments/paymentSlice";
import {
  getInvoiceData,
  deleteInvoice,
} from "../features/invoice/invoiceSlice";
import { getStates } from "../features/statecity/statecitySlice";
import { getUsers } from "../features/cutomers/customerSlice";
import { Chart } from "chart.js/auto";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Select,
  MenuItem,
  Button,
  Checkbox,
} from "@mui/material";
import UploadInvoiceAccordion from "./UploadInvoiceAccordion";
import CsvValidationModal from "../components/CsvValidationModal";

const InvoiceManagementDashboard = () => {
  const dispatch = useDispatch();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [csvValidationErrors, setCsvValidationErrors] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const {
    invoicedatas,
    isLoading: invoiceIsLoading,
    isError: invoiceIsError,
    message: invoiceErrorMessage,
  } = useSelector(
    (state) =>
      state.invoice || {
        invoicedatas: [],
        isLoading: false,
        isError: false,
        message: "",
      }
  );

  const { user } = useSelector((state) => state.auth);

  // Helper functions for access control
  const canUploadInvoice = () => {
    return user?.role === "superadmin" || 
           user?.role === "super-admin" || 
           user?.role === "Super Admin" || 
           user?.email === "accounts.entry@lnjskills.com";
  };

  const canEditDelete = () => {
    return user?.role === "superadmin" || 
           user?.role === "super-admin" || 
           user?.role === "Super Admin";
  };

  // Debug: Check user role
  useEffect(() => {
    console.log("Current user:", user);
    console.log("User role:", user?.role);
  }, [user]);

  const { csvDatas, Loading: uploadLoading } = useSelector(
    (state) => state.payment || { csvDatas: null, Loading: false }
  );

  const { statelist } = useSelector((state) => state.statecity);
  const { customers: vclist } = useSelector((state) => state.customer);

  // Chart refs & robust refresh helper with retries
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const [chartRefreshKey, setChartRefreshKey] = useState(0);

  const refreshChart = (attempt = 0) => {
    const MAX_ATTEMPTS = 8;
    const DELAY = 80; // ms
    window.setTimeout(() => {
      try {
        const canvas = chartRef.current;
        if (!canvas) {
          if (attempt < MAX_ATTEMPTS) {
            refreshChart(attempt + 1);
          } else {
            setChartRefreshKey((k) => k + 1);
          }
          return;
        }
        const rect = canvas.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
          if (attempt < MAX_ATTEMPTS) {
            refreshChart(attempt + 1);
            return;
          } else {
            setChartRefreshKey((k) => k + 1);
            return;
          }
        }
        if (chartInstanceRef.current) {
          try {
            chartInstanceRef.current.update();
            chartInstanceRef.current.resize();
          } catch (err) {
            try {
              chartInstanceRef.current.destroy();
            } catch (e) {}
            chartInstanceRef.current = null;
            setChartRefreshKey((k) => k + 1);
          }
        } else {
          setChartRefreshKey((k) => k + 1);
        }
      } catch (e) {
        if (attempt < MAX_ATTEMPTS) {
          refreshChart(attempt + 1);
        } else {
          setChartRefreshKey((k) => k + 1);
        }
      }
    }, DELAY);
  };

  const [filters, setFilters] = useState({
    state: "",
    district: "",
    vc: "",
    fromDate: "",
    toDate: "",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [invoiceSearchInput, setInvoiceSearchInput] = useState(""); // Temporary input value
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ open: false, invoiceNo: null });
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [bulkDeleteModal, setBulkDeleteModal] = useState({ open: false, type: '' }); // type: 'selected' or 'all'

  // Gap Metrics State
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredGapCard, setHoveredGapCard] = useState(null);
  const [gapMetricsLoading, setGapMetricsLoading] = useState(true);
  const [gapMetrics, setGapMetrics] = useState({
    avgSalaryDueToPaid: 0,
    avgSalaryPaidToBillGen: 0,
    avgBillGenToSubmission: 0,
    totalCycleTime: 0,
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? "N/A" : date.toISOString().split("T")[0];
  };

  useEffect(() => {
    if (invoicedatas?.data?.invoices) {
      setFilteredInvoices(invoicedatas.data.invoices);
      refreshChart();
    } else {
      setFilteredInvoices([]);
    }
  }, [invoicedatas]);

  // Initial fetch
  useEffect(() => {
    dispatch(getInvoiceData({}))
      .unwrap()
      .catch((error) => {
        console.error("Failed to fetch invoices:", error);
        toast.error(`Failed to fetch invoices: ${error.msg || "Please try again."}`, { autoClose: 3000 });
        setTimeout(() => refreshChart(), 100);
      });
    dispatch(getStates());
    dispatch(getUsers());
  }, [dispatch]);

  // Gap Metrics fetch Effect
  useEffect(() => {
    console.log('🎯 Gap Metrics useEffect triggered!', {
      invoiceSearch,
      filtersState: filters.state,
      filteredInvoicesCount: filteredInvoices.length,
      refreshTrigger
    });
    
    const fetchGapMetrics = async () => {
      try {
        setGapMetricsLoading(true);
        const queryParams = new URLSearchParams();
        if (filters.state) queryParams.append('state', normalizeState(filters.state));
        if (filters.district) queryParams.append('district', filters.district);
        if (filters.vc) queryParams.append('vc_name', filters.vc);

        // Priority 1: If rows are SELECTED, use only selected rows
        if (selectedInvoiceIds.length > 0) {
          const selectedInvoiceNumbers = filteredInvoices
            .filter(inv => selectedInvoiceIds.includes(inv._id))
            .map(inv => inv.invoice_no)
            .filter(Boolean);
          
          if (selectedInvoiceNumbers.length > 0) {
            queryParams.append('invoice_numbers', selectedInvoiceNumbers.join(','));
          }
        }
        // Priority 2: If invoice search is active
        else if (invoiceSearch) {
          const filteredInvoiceNumbers = filteredInvoices
            .filter((inv) => 
              (inv.invoice_no || "").toLowerCase() === invoiceSearch.toLowerCase().trim()
            )
            .map((inv) => inv.invoice_no)
            .filter(Boolean);
          
          if (filteredInvoiceNumbers.length > 0) {
            queryParams.append('invoice_numbers', filteredInvoiceNumbers.join(','));
          } else {
            queryParams.append('invoice_numbers', '__NO_MATCH__');
          }
        }
        // Priority 3: Filtered invoices
        else if (filteredInvoices && filteredInvoices.length > 0) {
          const allFilteredInvoiceNumbers = filteredInvoices
            .map(inv => inv.invoice_no)
            .filter(Boolean);
          
          if (allFilteredInvoiceNumbers.length > 0) {
            queryParams.append('invoice_numbers', allFilteredInvoiceNumbers.join(','));
          }
        }

        const url = `${base_url}invoince/gapMetrics?${queryParams.toString()}`;
        const response = await axios.get(url, config);

        if (response.data.success) {
          setGapMetrics({
            ...response.data.data,
            _timestamp: Date.now(),
          });
        }
      } catch (error) {
        console.error("Failed to fetch gap metrics:", error);
      } finally {
        setGapMetricsLoading(false);
      }
    };
    
    fetchGapMetrics();
  }, [invoiceSearch, filters.state, filters.district, filters.vc, filteredInvoices, selectedInvoiceIds, refreshTrigger]);

  // Chart effect (re-creates on filteredInvoices, isMobile or chartRefreshKey change)
  useEffect(() => {
    const canvas = chartRef.current;
    if (!canvas) {
      refreshChart();
      return;
    }

    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      refreshChart();
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      refreshChart();
      return;
    }

    if (chartInstanceRef.current) {
      try {
        chartInstanceRef.current.destroy();
      } catch (err) {}
      chartInstanceRef.current = null;
    }

    // compute metrics - use selected invoices if any are selected, otherwise use all filtered
    const invoicesToCalculate = selectedInvoiceIds.length > 0
      ? filteredInvoices.filter(inv => selectedInvoiceIds.includes(inv._id))
      : filteredInvoices;
    
    const totalAmountCollected = invoicesToCalculate.reduce(
      (acc, cur) => acc + (cur?.payment_received || 0),
      0
    );
    const totalAgingAmount = invoicesToCalculate.reduce(
      (acc, cur) => acc + (cur?.invoice_amount || 0),
      0
    );
    const totalAmountDeducted = invoicesToCalculate.reduce(
      (acc, cur) => acc + (cur?.amount_deducted || 0),
      0
    );
    const totalPendingCollection = totalAgingAmount - totalAmountCollected - totalAmountDeducted;

    const invoicesWithAging = invoicesToCalculate.filter(
      (invoice) => Number(invoice?.aging) > 0
    );
    const totalAgingDaysSum = invoicesWithAging.reduce(
      (acc, cur) => acc + (Number(cur?.aging) || 0),
      0
    );
    const averageAgingDays =
      invoicesWithAging.length > 0
        ? Math.round(totalAgingDaysSum / invoicesWithAging.length)
        : 0;

    // create chart instance
    try {
      chartInstanceRef.current = new Chart(ctx, {
        type: "bar",
        data: {
          labels: [
            "Total Invoices Raised",
            "Total Amount Collected",
            "Average Aging Days",
            "Total Pending Collection",
          ],
          datasets: [
            {
              label: "Invoice Metrics",
              data: [
                totalAgingAmount,
                totalAmountCollected,
                averageAgingDays,
                totalPendingCollection,
              ],
              backgroundColor: ["#38a169", "#3182ce", "#dd6b20", "#e53e3e"],
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (tooltipItem) =>
                  tooltipItem.label === "Total Invoices Raised"
                    ? `${tooltipItem.label}: ${tooltipItem.raw}`
                    : tooltipItem.label === "Average Aging Days"
                    ? `${tooltipItem.label}: ${tooltipItem.raw} days`
                    : `${
                        tooltipItem.label
                      }: ₹${tooltipItem.raw.toLocaleString()}`,
              },
            },
          },
          scales: {
            x: { title: { display: true, text: "Metrics" } },
            y: {
              title: { display: true, text: "Value" },
              beginAtZero: true,
              ticks: {
                callback: (value) =>
                  value >= 1000 ? `₹${value.toLocaleString()}` : value,
                font: { size: isMobile ? 10 : 12 },
              },
            },
          },
        },
      });
    } catch (err) {
      console.error("Error creating chart:", err);
      refreshChart();
    }

    const onWinResize = () => {
      if (chartInstanceRef.current) {
        try {
          chartInstanceRef.current.resize();
        } catch (e) {}
      }
    };
    window.addEventListener("resize", onWinResize);

    return () => {
      window.removeEventListener("resize", onWinResize);
      if (chartInstanceRef.current) {
        try {
          chartInstanceRef.current.destroy();
        } catch (err) {}
        chartInstanceRef.current = null;
      }
    };
  }, [filteredInvoices, isMobile, chartRefreshKey, selectedInvoiceIds]);

  useEffect(() => {
    const handler = () => {
      setTimeout(() => setChartRefreshKey((k) => k + 1), 150);
    };
    window.addEventListener("refreshInvoiceChart", handler);
    return () => window.removeEventListener("refreshInvoiceChart", handler);
  }, [setChartRefreshKey]);

  const normalizeState = (state) => {
    if (!state) return state;
    const match = state.match(/^(.*?)\s*\((.*?)\)$/);
    return match ? match[2].trim() : state;
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleInvoiceSearch = () => {
    const val = invoiceSearchInput.trim();
    setInvoiceSearch(val);
    setCurrentPage(1);
    
    if (val.length >= 2) {
      axios.get(`${base_url}invoince/searchInvoice?invoice_no=${val}`, config)
        .then(res => {
          if (res.data?.data?.invoices) {
            setFilteredInvoices(res.data.data.invoices);
          }
        })
        .catch(() => {
          toast.error("Failed to search invoice", { autoClose: 3000 });
        });
    } else if (val.length === 0) {
      if (invoicedatas?.data?.invoices) {
        setFilteredInvoices(invoicedatas.data.invoices);
      }
    }
  };

  const handleApplyFilters = () => {
    if (
      filters.fromDate &&
      filters.toDate &&
      new Date(filters.fromDate) > new Date(filters.toDate)
    ) {
      toast.error("To Date cannot be earlier than From Date.", { autoClose: 3000 });
      refreshChart();
      return;
    }

    const query = {
      state: normalizeState(filters.state),
      district: filters.district,
      vc_name: filters.vc,
      fromDate: filters.fromDate || "",
      toDate: filters.toDate || "",
    };

    const filteredQuery = Object.fromEntries(
      Object.entries(query).filter(
        ([_, value]) => value !== "" && value !== undefined
      )
    );

    dispatch(getInvoiceData(filteredQuery))
      .unwrap()
      .then((response) => {
        let invoices = response?.data?.invoices || [];

        if (filters.fromDate || filters.toDate) {
          invoices = invoices.filter((invoice) => {
            if (!invoice.invoice_date) return false;
            const invoiceDate = new Date(invoice.invoice_date);
            const fromDate = filters.fromDate
              ? new Date(filters.fromDate)
              : null;
            const toDate = filters.toDate ? new Date(filters.toDate) : null;

            if (fromDate && toDate) {
              return invoiceDate >= fromDate && invoiceDate <= toDate;
            } else if (fromDate) {
              return invoiceDate >= fromDate;
            } else if (toDate) {
              return invoiceDate <= toDate;
            }
            return true;
          });
        }

        if (filters.vc) {
          invoices = invoices.filter((invoice) => {
            const vcName = invoice.vc_name?.name || invoice.vc_name || "";
            return vcName.toLowerCase() === filters.vc.toLowerCase();
          });
        }

        if (invoices.length > 0) {
          setFilteredInvoices(invoices);
          setCurrentPage(1);
          refreshChart();
        } else {
          toast.warning(
            "No invoices found for the selected filters. Display remains unchanged.",
            { autoClose: 3000 }
          );
          refreshChart();
        }
      })
      .catch((error) => {
        console.error("Failed to fetch filtered invoices:", error);
        toast.error(`Failed to apply filters: ${error.msg || "Please try again."}`, { autoClose: 3000 });
        setTimeout(() => refreshChart(), 100);
      });
  };

  const handleResetFilters = () => {
    setFilters({
      state: "",
      district: "",
      vc: "",
      fromDate: "",
      toDate: "",
    });
    dispatch(getInvoiceData({}))
      .unwrap()
      .then((response) => {
        if (response?.data?.invoices) {
          setFilteredInvoices(response.data.invoices);
          setCurrentPage(1);
          refreshChart();
        } else {
          toast.warning(
            "No invoices returned from server after reset. Display remains unchanged.",
            { autoClose: 3000 }
          );
          refreshChart();
        }
      })
      .catch((error) => {
        console.error("Failed to reset invoices:", error);
        toast.error(`Failed to reset filters: ${error.msg || "Please try again."}`, { autoClose: 3000 });
        setTimeout(() => refreshChart(), 100);
      });
  };

  const formatCurrency = (amount) =>
    amount !== undefined && amount !== null
      ? `₹ ${Number(amount).toLocaleString("en-IN")}`
      : "₹ 0";

  const headers = [
    "Select",
    "S.No.",
    "Invoice Date",
    "Invoice No.",
    "State",
    "District",
    "Vc_Name",
    "Bill Period",
    "Bill Type",
    "Invoice Amount (₹)",
    "TDS Deducted (₹)",
    "Amount Deducted (₹)",
    "Payment Received (₹)",
    "Received Date",
    "Submitted On",
    "Submitted To",
    "Contact Number",
    "Courier Company",
    "Consignment No.",
    "Balance (₹)",
    "Aging (Days)",
    "Created by",
    "Remarks",
    "Action",
  ];

  const handleSort = (key, index) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });

    const sortedInvoices = [...filteredInvoices].sort((a, b) => {
      let aValue = a[key] || "";
      let bValue = b[key] || "";
      if (index === 1) {
        aValue = (currentPage - 1) * pageSize + filteredInvoices.indexOf(a) + 1;
        bValue = (currentPage - 1) * pageSize + filteredInvoices.indexOf(b) + 1;
      } else if (
        [
          "invoice_amount",
          "tds",
          "amount_deducted",
          "payment_received",
          "balance",
        ].includes(key)
      ) {
        aValue = Number(a[key] || 0);
        bValue = Number(b[key] || 0);
      } else if (key === "vc_name") {
        aValue = a.vc_name?.name || a.vc_name || "";
        bValue = b.vc_name?.name || b.vc_name || "";
      } else if (
        ["invoice_date", "recived_date", "submitted_date"].includes(key)
      ) {
        aValue = a[key] ? new Date(a[key]).getTime() : 0;
        bValue = b[key] ? new Date(b[key]).getTime() : 0;
      }
      return direction === "asc"
        ? aValue > bValue
          ? 1
          : -1
        : aValue < bValue
        ? 1
        : -1;
    });
    setFilteredInvoices(sortedInvoices);
    refreshChart();
  };

  const handleViewImage = (photoUrl) => {
    if (photoUrl && photoUrl !== "none" && photoUrl !== "null" && photoUrl !== "undefined" && photoUrl !== "N/A" && photoUrl.trim() !== "") {
      window.open(photoUrl, "_blank");
    } else {
      toast.warning("No file available to view.", { autoClose: 3000 });
      refreshChart();
    }
  };

  const handleUploadPdf = (invoiceNo) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.pdf';
    fileInput.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error('Please select a PDF file only!', { autoClose: 3000 });
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size should be less than 10MB!', { autoClose: 3000 });
        return;
      }

      const formData = new FormData();
      formData.append('invoiceFile', file);
      formData.append('invoice_no', invoiceNo);

      setIsUploading(true);

      axios.put(`${base_url}invoince/updateInvoicePhoto`, formData, {
        ...config,
        headers: {
          ...config.headers,
          'Content-Type': 'multipart/form-data',
        },
      })
        .then(() => {
          toast.success(`PDF uploaded successfully for invoice ${invoiceNo}!`, { autoClose: 3000 });
          dispatch(getInvoiceData({}))
            .unwrap()
            .then((response) => {
              if (response?.data?.invoices) {
                setFilteredInvoices(response.data.invoices);
                refreshChart();
              }
            })
            .finally(() => {
              setIsUploading(false);
            });
        })
        .catch((error) => {
          console.error('Failed to upload PDF:', error);
          toast.error(`Failed to upload PDF: ${error.response?.data?.msg || error.message || 'Please try again.'}`, { autoClose: 3000 });
          setIsUploading(false);
          setTimeout(() => refreshChart(), 100);
        });
    };
    fileInput.click();
  };

  const getDistrictsForEditState = () => {
    if (!editingInvoice?.state || !statelist) return [];
    const selectedState = statelist.find(
      (state) => state.state_name === editingInvoice.state || state.state_name.includes(editingInvoice.state)
    );
    return selectedState ? selectedState.district_name : [];
  };

  const getVCsForEditState = () => {
    if (!editingInvoice?.state || !vclist) return [];
    const normalizedState = editingInvoice.state.match(/\(([^)]+)\)/)?.[1] || editingInvoice.state;
    return vclist.filter((vc) => vc.role === "vc" && vc.state === normalizedState);
  };

  const handleEdit = (invoice) => {
    setEditingInvoice({
      ...invoice,
      original_invoice_no: invoice.invoice_no // Store original invoice number
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = () => {
    if (!editingInvoice) return;

    const updateData = {
      invoice_no: editingInvoice.original_invoice_no || editingInvoice.invoice_no, // Use original for lookup
      new_invoice_no: editingInvoice.invoice_no, // Send new invoice number
      invoice_date: editingInvoice.invoice_date || "",
      state: editingInvoice.state || "",
      district: editingInvoice.district || "",
      vc_name: typeof editingInvoice.vc_name === 'object' ? editingInvoice.vc_name?.name : editingInvoice.vc_name || "",
      bill_period: editingInvoice.bill_period || "",
      bill_type: editingInvoice.bill_type || "",
      invoice_amount: parseFloat(editingInvoice.invoice_amount) || 0,
      tds: parseFloat(editingInvoice.tds) || 0,
      amount_deducted: parseFloat(editingInvoice.amount_deducted) || 0,
      amount_received: parseFloat(editingInvoice.payment_received) || 0,
      recived_date: editingInvoice.recived_date || "",
      submitted_on: editingInvoice.submitted_date || "",
      submitted_to: editingInvoice.submitted_to || "",
      contact_no: editingInvoice.contact_no || "",
      courier_company: editingInvoice.courier_company || "",
      consignment_no: editingInvoice.consignment_no || "",
      remarks: editingInvoice.remarks || "",
    };

    axios.put(`${base_url}invoince/updateInvoice`, updateData, config)
      .then(() => {
        toast.success(`Invoice ${editingInvoice.invoice_no} updated successfully!`, { autoClose: 3000 });
        setEditModalOpen(false);
        setEditingInvoice(null);
        dispatch(getInvoiceData({}))
          .unwrap()
          .then((response) => {
            if (response?.data?.invoices) {
              setFilteredInvoices(response.data.invoices);
              refreshChart();
            }
          });
      })
      .catch((error) => {
        console.error("Failed to update invoice:", error);
        toast.error(`Failed to update invoice: ${error.response?.data?.msg || error.message || "Please try again."}`, { autoClose: 3000 });
      });
  };

  const handleDelete = (invoiceNo) => {
    setDeleteConfirmModal({ open: true, invoiceNo });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const currentPageInvoices = filteredInvoices
        .filter(inv => !invoiceSearch || (inv.invoice_no || "").toLowerCase() === invoiceSearch.toLowerCase().trim())
        .slice((currentPage - 1) * pageSize, currentPage * pageSize);
      const allIds = currentPageInvoices.map(inv => inv._id).filter(Boolean);
      setSelectedInvoiceIds(allIds);
    } else {
      setSelectedInvoiceIds([]);
    }
  };

  const handleSelectInvoice = (invoiceId) => {
    setSelectedInvoiceIds(prev => {
      if (prev.includes(invoiceId)) {
        return prev.filter(id => id !== invoiceId);
      } else {
        return [...prev, invoiceId];
      }
    });
  };

  const handleBulkDelete = (type) => {
    if (type === 'selected' && selectedInvoiceIds.length === 0) {
      toast.warning("Please select at least one invoice to delete.", { autoClose: 3000 });
      return;
    }
    setBulkDeleteModal({ open: true, type });
  };

  const confirmBulkDelete = () => {
    setBulkDeleteModal({ open: false, type: '' });

    const idsToDelete = selectedInvoiceIds;

    if (idsToDelete.length === 0) {
      toast.warning("No invoices to delete.", { autoClose: 3000 });
      return;
    }

    axios.delete(`${base_url}invoince/bulkDeleteInvoices`, {
      ...config,
      data: { invoice_ids: idsToDelete }
    })
      .then(() => {
        toast.success(`Successfully deleted ${idsToDelete.length} invoice(s)!`, { autoClose: 3000 });
        setSelectedInvoiceIds([]);
        dispatch(getInvoiceData({}))
          .unwrap()
          .then((response) => {
            if (response?.data?.invoices) {
              setFilteredInvoices(response.data.invoices);
              refreshChart();
            }
          });
      })
      .catch((error) => {
        console.error("Failed to bulk delete invoices:", error);
        toast.error(`Failed to delete invoices: ${error.response?.data?.msg || error.message || "Please try again."}`, { autoClose: 3000 });
        setTimeout(() => refreshChart(), 100);
      });
  };

  const confirmDelete = () => {
    const invoiceNo = deleteConfirmModal.invoiceNo;
    setDeleteConfirmModal({ open: false, invoiceNo: null });
    
    dispatch(deleteInvoice({ invoice_no: invoiceNo }))
      .unwrap()
      .then(() => {
        toast.success(`Invoice ${invoiceNo} deleted successfully!`, { autoClose: 3000 });
        dispatch(getInvoiceData({}))
            .unwrap()
            .then((response) => {
              if (response?.data?.invoices) {
                setFilteredInvoices(response.data.invoices);
                refreshChart();
              } else {
                toast.warning(
                  "No invoices returned after delete refresh. Keeping current view.",
                  { autoClose: 3000 }
                );
                refreshChart();
              }
            })
            .catch((error) => {
              console.error("Failed to refetch invoices:", error);
              toast.error(
                `Failed to refresh invoices after delete: ${
                  error.message || "Please try again."
                }. Keeping current view.`,
                { autoClose: 3000 }
              );
              refreshChart();
            });
      })
      .catch((error) => {
        console.error("Failed to delete invoice:", error);
        toast.error(
          `Failed to delete invoice: ${error.message || "Please try again."}`,
          { autoClose: 3000 }
        );
        setTimeout(() => refreshChart(), 100);
      });
  };

  const downloadTableCSV = () => {
    const csvData = filteredInvoices.map((invoice, index) => [
      index + 1,
      invoice.invoice_date
        ? new Date(invoice.invoice_date).toLocaleDateString("en-IN")
        : "N/A",
      invoice.invoice_no || "N/A",
      invoice.state || "N/A",
      invoice.district || "N/A",
      invoice.vc_name?.name || invoice.vc_name || "N/A",
      invoice.bill_period || "N/A",
      invoice.bill_type || "N/A",
      invoice.invoice_amount || 0,
      invoice.tds || 0,
      invoice.amount_deducted || 0,
      invoice.payment_received || 0,
      formatDate(invoice.recived_date),
      invoice.submitted_date || "N/A",
      invoice.submitted_to || "N/A",
      invoice.contact_no || "N/A",
      invoice.courier_company || "N/A",
      invoice.consignment_no || "N/A",
      invoice.balance !== undefined && invoice.balance !== null
        ? invoice.balance
        : "N/A",
      invoice.aging || "N/A",
      invoice.created_by?.email || " ",
      invoice.remarks || "N/A",
    ]);

    const csvContent = [
      headers.slice(1).join(","), // Omit select
      ...csvData.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "invoice_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const downloadPDF = () => {
    const doc = new jsPDF("landscape", "mm", "a4");
    doc.setFont("helvetica");
    doc.setFontSize(16);
    doc.text("Invoice Management Dashboard", 14, 10);

    const formatPDFNumber = (value) => {
      if (value === null || value === undefined || value === "") return "0";
      const num = parseFloat(value);
      if (isNaN(num)) return "0";
      return num.toFixed(0);
    };

    const cardData = [
      ["Total Invoices Raised", `${overviewData.totalInvoices} (Rs ${formatPDFNumber(overviewData.totalAgingAmount)})`],
      ["Total Amount Collected", `Rs ${formatPDFNumber(overviewData.totalAmountCollected)}`],
      ["TDS Deducted", `Rs ${formatPDFNumber(totalTDSDeducted)}`],
      ["Amount Deducted", `Rs ${formatPDFNumber(totalAmountDeducted)}`],
      ["Average Aging Days", `${averageAgingDays} days`],
      ["Total Pending Collection", `Rs ${formatPDFNumber(overviewData.totalPendingCollection)}`],
    ];

    doc.autoTable({
      startY: 15,
      head: [["Metric", "Value"]],
      body: cardData,
      theme: "grid",
      styles: { fontSize: 10, cellPadding: 2, font: "helvetica" },
      headStyles: { fillColor: [66, 66, 66] },
      columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 100 } },
    });

    const chartCanvas = chartRef.current;
    try {
      const chartImage = chartCanvas.toDataURL("image/png");
      doc.addImage(
        chartImage,
        "PNG",
        14,
        doc.lastAutoTable.finalY + 5,
        270,
        60
      );
    } catch (err) {
      console.warn("Chart image not available for PDF:", err);
    }

    const formatPDFCurrency = (value) => {
      if (value === null || value === undefined || value === "") return "N/A";
      const num = parseFloat(value);
      if (isNaN(num)) return "N/A";
      return `Rs ${num.toFixed(0)}`;
    };

    const pdfHeaders = [
      "S.No.",
      "Invoice Date",
      "Invoice No.",
      "State",
      "District",
      "Vc_Name",
      "Bill Period",
      "Bill Type",
      "Invoice Amount",
      "TDS Deducted",
      "Amount Deducted",
      "Payment Received",
      "Received Date",
      "Submitted On",
      "Submitted To",
      "Contact Number",
      "Courier Company",
      "Consignment No.",
      "Balance",
      "Aging (Days)",
      "Created by",
      "Remarks",
    ];

    const tableData = filteredInvoices.map((invoice, index) => [
      index + 1,
      invoice.invoice_date
        ? new Date(invoice.invoice_date).toLocaleDateString("en-IN")
        : "N/A",
      invoice.invoice_no || "N/A",
      invoice.state || "N/A",
      invoice.district || "N/A",
      invoice.vc_name?.name || invoice.vc_name || "N/A",
      invoice.bill_period || "N/A",
      invoice.bill_type || "N/A",
      formatPDFCurrency(invoice.invoice_amount),
      formatPDFCurrency(invoice.tds),
      formatPDFCurrency(invoice.amount_deducted),
      formatPDFCurrency(invoice.payment_received),
      formatDate(invoice.recived_date),
      invoice.submitted_date || "N/A",
      invoice.submitted_to || "N/A",
      invoice.contact_no || "N/A",
      invoice.courier_company || "N/A",
      invoice.consignment_no || "N/A",
      formatPDFCurrency(invoice.balance),
      invoice.aging || "N/A",
      invoice.created_by?.email || " ",
      invoice.remarks || "N/A",
    ]);

    doc.autoTable({
      startY: doc.lastAutoTable ? doc.lastAutoTable.finalY + 68 : 80,
      head: [pdfHeaders],
      body: tableData,
      theme: "striped",
      styles: { 
        fontSize: 5.5, 
        cellPadding: 1,
        font: "helvetica",
        overflow: "linebreak",
        cellWidth: "wrap",
        halign: "left"
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontSize: 5.5,
        fontStyle: "bold",
        halign: "center"
      },
      margin: { left: 5, right: 5 },
      columnStyles: {
        0: { cellWidth: 7 },   // S.No
        1: { cellWidth: 15 },  // Invoice Date
        2: { cellWidth: 20 },  // Invoice No
        3: { cellWidth: 15 },  // State
        4: { cellWidth: 12 },  // District
        5: { cellWidth: 18 },  // VC Name
        6: { cellWidth: 13 },  // Bill Period
        7: { cellWidth: 11 },  // Bill Type
        8: { cellWidth: 16 },  // Invoice Amount
        9: { cellWidth: 13 },  // TDS
        10: { cellWidth: 14 }, // Amount Deducted
        11: { cellWidth: 16 }, // Payment Received
        12: { cellWidth: 15 }, // Received Date
        13: { cellWidth: 15 }, // Submitted On
        14: { cellWidth: 13 }, // Submitted To
        15: { cellWidth: 13 }, // Contact
        16: { cellWidth: 16 }, // Courier
        17: { cellWidth: 13 }, // Consignment
        18: { cellWidth: 14 }, // Balance
        19: { cellWidth: 10 }, // Aging
        20: { cellWidth: 22 }, // Created by
        21: { cellWidth: 18 }  // Remarks
      },
      didDrawPage: (data) => {
        doc.setFontSize(8);
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        doc.text(
          `Page ${data.pageNumber}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: "center" }
        );
      },
    });

    doc.save("invoice_report.pdf");
  };

  const downloadCsvTemplate = () => {
    dispatch(csvDownloads())
      .unwrap()
      .then((response) => {
        const blob = new Blob([response], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "invoice_template.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        console.error("Error downloading CSV:", err);
        toast.error(`Failed to download CSV: ${err.message || "Please try again."}`, { autoClose: 3000 });
        refreshChart();
      });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
  };

  const handleImport = () => {
    if (!selectedFile) {
      toast.warning("Please select a CSV file to upload.", { autoClose: 3000 });
      return;
    }
    if (selectedFile.size === 0) {
      toast.error("The selected CSV file is empty. Please upload a valid file.", { autoClose: 3000 });
      return;
    }
    if (!selectedFile.name.endsWith(".csv")) {
      toast.error("Please upload a valid CSV file.", { autoClose: 3000 });
      return;
    }

    const formData = new FormData();
    formData.append("invoiceFile", selectedFile);

    dispatch(uploadCsvs(formData))
      .unwrap()
      .then((response) => {
        if (response?.data?.invoices) {
          setFilteredInvoices((prev) => {
            const merged = [...prev, ...response.data.invoices];
            return merged;
          });
          setCurrentPage(1);
          toast.success(
            `CSV file uploaded successfully! ${response.data.invoices.length} invoice(s) added.`,
            { autoClose: 3000 }
          );
          refreshChart();
        } else {
          dispatch(getInvoiceData({}))
            .unwrap()
            .then((result) => {
              if (result?.data?.invoices) {
                setFilteredInvoices(result.data.invoices);
                setCurrentPage(1);
                refreshChart();
                toast.success(`CSV file processed and invoices refreshed.`, { autoClose: 3000 });
              } else {
                toast.warning(
                  "CSV uploaded but server returned no invoices. Display remains unchanged.",
                  { autoClose: 3000 }
                );
                refreshChart();
              }
            })
            .catch((error) => {
              console.error("Failed to fetch updated invoices:", error);
              toast.warning(
                "CSV uploaded but failed to refresh invoice data. Keeping previous view.",
                { autoClose: 3000 }
              );
              refreshChart();
            });
        }
        setFilters({
          state: "",
          district: "",
          vc: "",
          fromDate: "",
          toDate: "",
        });
        setSelectedFile(null);
      })
      .catch((err) => {
        console.error("Failed to upload CSV:", err);
        const errorData = err?.errors
          ? err
          : err?.response?.data?.errors
          ? err.response.data
          : null;

        if (errorData && errorData.errors && errorData.errors.length > 0) {
          setCsvValidationErrors(errorData);
          toast.error(`CSV upload rejected: ${errorData.errors.length} validation issue(s) found.`, {
            autoClose: 4000,
          });
        } else {
          toast.error(
            `Failed to upload CSV: ${
              err.response?.data?.message || err.message || "Please try again."
            }`,
            { autoClose: 4000 }
          );
        }
        setTimeout(() => refreshChart(), 100);
      });
  };

  // Direct print layouts
  const handlePrint = () => {
    const currentDate = new Date().toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice Management Report</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
            padding: 40px;
            background: #ffffff;
            color: #1a2332;
          }
          .report-header {
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #eef2f6;
          }
          .report-title {
            font-size: 24px;
            font-weight: 700;
            color: #1a2332;
            margin-bottom: 4px;
          }
          .report-subtitle {
            font-size: 14px;
            color: #64748b;
          }
          .report-meta {
            display: flex;
            gap: 20px;
            margin-top: 8px;
            font-size: 13px;
            color: #475569;
          }
          .summary-grid {
            display: grid;
            grid-template-columns: repeat(6, 1fr);
            gap: 16px;
            margin: 24px 0;
          }
          .summary-card {
            background: #f8fafc;
            padding: 16px;
            border-radius: 8px;
            border: 1px solid #eef2f6;
            text-align: center;
          }
          .summary-label {
            font-size: 12px;
            color: #64748b;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.03em;
          }
          .summary-value {
            font-size: 16px;
            font-weight: 700;
            color: #1a2332;
            margin-top: 4px;
          }
          .summary-value.blue { color: #3b82f6; }
          .summary-value.green { color: #10b981; }
          .summary-value.purple { color: #8b5cf6; }
          .summary-value.orange { color: #f59e0b; }
          .summary-value.teal { color: #06b6d4; }
          .summary-value.red { color: #ef4444; }
          
          .gap-metrics {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 16px;
            margin: 24px 0;
          }
          .metric-card {
            background: #f8fafc;
            padding: 16px;
            border-radius: 8px;
            border: 1px solid #eef2f6;
            text-align: center;
          }
          .metric-label {
            font-size: 12px;
            color: #64748b;
            font-weight: 500;
          }
          .metric-value {
            font-size: 18px;
            font-weight: 700;
            color: #1a2332;
            margin-top: 4px;
          }
          
          .table-container {
            margin-top: 24px;
            overflow-x: auto;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 9px;
          }
          thead {
            background: #f8fafc;
          }
          th {
            padding: 8px 10px;
            text-align: left;
            font-weight: 600;
            color: #475569;
            border-bottom: 2px solid #eef2f6;
            white-space: nowrap;
            text-transform: uppercase;
            font-size: 8px;
            letter-spacing: 0.03em;
          }
          td {
            padding: 6px 10px;
            border-bottom: 1px solid #f0f0f0;
            color: #1a2332;
          }
          tr:hover {
            background: #fafbfc;
          }
          .text-right {
            text-align: right;
          }
          .footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #eef2f6;
            display: flex;
            justify-content: space-between;
            font-size: 12px;
            color: #94a3b8;
          }
          @media print {
            body { padding: 20px; }
            .no-print { display: none; }
            .summary-grid { break-inside: avoid; }
            .gap-metrics { break-inside: avoid; }
            table { break-inside: auto; }
            tr { break-inside: avoid; }
          }
          @media (max-width: 768px) {
            .summary-grid { grid-template-columns: repeat(3, 1fr); }
            .gap-metrics { grid-template-columns: 1fr; }
          }
        </style>
      </head>
      <body>
        <div class="report-header">
          <div class="report-title">📊 Invoice Management Report</div>
          <div class="report-subtitle">Comprehensive invoice summary and details</div>
          <div class="report-meta">
            <span>📅 Generated: ${currentDate}</span>
            <span>📋 Records: ${filteredInvoices.length}</span>
            <span>💰 Total Amount Raised: ₹${overviewData.totalAgingAmount.toLocaleString()}</span>
          </div>
        </div>

        <div class="summary-grid">
          ${[
            { label: "Total Invoices", value: overviewData.totalInvoices.toString(), color: "blue" },
            { label: "Total Raised", value: `₹${overviewData.totalAgingAmount.toLocaleString()}`, color: "teal" },
            { label: "Total Collected", value: `₹${overviewData.totalAmountCollected.toLocaleString()}`, color: "green" },
            { label: "TDS Deducted", value: `₹${totalTDSDeducted.toLocaleString()}`, color: "purple" },
            { label: "Amount Deducted", value: `₹${totalAmountDeducted.toLocaleString()}`, color: "orange" },
            { label: "Pending Collection", value: `₹${overviewData.totalPendingCollection.toLocaleString()}`, color: "red" }
          ].map(item => `
            <div class="summary-card">
              <div class="summary-label">${item.label}</div>
              <div class="summary-value ${item.color}">${item.value}</div>
            </div>
          `).join('')}
        </div>

        <div class="gap-metrics">
          ${[
            { label: "Salary Due → Paid", value: gapMetrics.avgSalaryDueToPaid !== null ? `${gapMetrics.avgSalaryDueToPaid} days` : "N/A" },
            { label: "Salary Paid → Bill Gen", value: gapMetrics.avgSalaryPaidToBillGen !== null ? `${gapMetrics.avgSalaryPaidToBillGen} days` : "N/A" },
            { label: "Bill Gen → Submission", value: gapMetrics.avgBillGenToSubmission !== null ? `${gapMetrics.avgBillGenToSubmission} days` : "N/A" }
          ].map(item => `
            <div class="metric-card">
              <div class="metric-label">${item.label}</div>
              <div class="metric-value">${item.value}</div>
            </div>
          `).join('')}
        </div>

        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Invoice Date</th>
                <th>Invoice No.</th>
                <th>State</th>
                <th>District</th>
                <th>Vc Name</th>
                <th>Bill Period</th>
                <th>Bill Type</th>
                <th>Invoice Amount</th>
                <th>TDS Deducted</th>
                <th>Amount Deducted</th>
                <th>Payment Received</th>
                <th>Received Date</th>
                <th>Submitted On</th>
                <th>Submitted To</th>
                <th>Contact</th>
                <th>Courier</th>
                <th>Consignment No.</th>
                <th>Balance</th>
                <th>Aging (Days)</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              ${filteredInvoices.map((invoice, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td>${invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString("en-IN") : "N/A"}</td>
                  <td>${invoice.invoice_no || "N/A"}</td>
                  <td>${invoice.state || "N/A"}</td>
                  <td>${invoice.district || "N/A"}</td>
                  <td>${invoice.vc_name?.name || invoice.vc_name || "N/A"}</td>
                  <td>${invoice.bill_period || "N/A"}</td>
                  <td>${invoice.bill_type || "N/A"}</td>
                  <td class="text-right">₹${(invoice.invoice_amount || 0).toLocaleString()}</td>
                  <td class="text-right">₹${(invoice.tds || 0).toLocaleString()}</td>
                  <td class="text-right">₹${(invoice.amount_deducted || 0).toLocaleString()}</td>
                  <td class="text-right">₹${(invoice.payment_received || 0).toLocaleString()}</td>
                  <td>${formatDate(invoice.recived_date)}</td>
                  <td>${invoice.submitted_date || "N/A"}</td>
                  <td>${invoice.submitted_to || "N/A"}</td>
                  <td>${invoice.contact_no || "N/A"}</td>
                  <td>${invoice.courier_company || "N/A"}</td>
                  <td>${invoice.consignment_no || "N/A"}</td>
                  <td class="text-right">₹${(invoice.balance || 0).toLocaleString()}</td>
                  <td>${invoice.aging || "N/A"}</td>
                  <td>${invoice.remarks || "N/A"}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <div class="footer">
          <span>Generated on ${new Date().toLocaleString()}</span>
          <span>${filteredInvoices.length} records</span>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    } else {
      toast.error('Please allow pop-ups for printing', { autoClose: 3000 });
    }
  };

  const invoicesForKPI = selectedInvoiceIds.length > 0
    ? filteredInvoices.filter(inv => selectedInvoiceIds.includes(inv._id))
    : filteredInvoices;

  const overviewData = {
    totalInvoices: invoicesForKPI.length,
    totalAmountCollected: invoicesForKPI.reduce(
      (acc, cur) => acc + (cur?.payment_received || 0),
      0
    ),
    totalAgingAmount: invoicesForKPI.reduce(
      (acc, cur) => acc + (cur?.invoice_amount || 0),
      0
    ),
    totalPendingCollection:
      invoicesForKPI.reduce(
        (acc, cur) => acc + (cur?.invoice_amount || 0),
        0
      ) -
      invoicesForKPI.reduce(
        (acc, cur) => acc + (cur?.payment_received || 0),
        0
      ) -
      invoicesForKPI.reduce(
        (acc, cur) => acc + (cur?.amount_deducted || 0),
        0
      ),
  };

  const invoicesWithAging = invoicesForKPI.filter(
    (invoice) => Number(invoice?.aging) > 0
  );
  const totalAgingDaysSum = invoicesWithAging.reduce(
    (acc, cur) => acc + (Number(cur?.aging) || 0),
    0
  );
  const averageAgingDays =
    invoicesWithAging.length > 0
      ? Math.round(totalAgingDaysSum / invoicesWithAging.length)
      : 0;

  const totalTDSDeducted = invoicesForKPI.reduce(
    (acc, cur) => acc + (cur?.tds || 0),
    0
  );

  const totalAmountDeducted = invoicesForKPI.reduce(
    (acc, cur) => acc + (cur?.amount_deducted || 0),
    0
  );

  const cardsData = [
    {
      label: "Total Invoices Raised",
      value: `${overviewData.totalInvoices} (${formatCurrency(
        overviewData.totalAgingAmount
      )})`,
      color: "#3b82f6",
      icon: "💰"
    },
    {
      label: "Total Amount Collected",
      value: formatCurrency(overviewData.totalAmountCollected),
      color: "#10b981",
      icon: "✅"
    },
    {
      label: "TDS Deducted",
      value: formatCurrency(totalTDSDeducted),
      color: "#8b5cf6",
      icon: "📊"
    },
    {
      label: "Amount Deducted",
      value: formatCurrency(totalAmountDeducted),
      color: "#f59e0b",
      icon: "🔄"
    },
    {
      label: "Average Aging Days",
      value: `${averageAgingDays} days`,
      color: "#06b6d4",
      icon: "📅"
    },
    {
      label: "Total Pending Collection",
      value: formatCurrency(overviewData.totalPendingCollection),
      color: "#ef4444",
      icon: "⚠️"
    },
  ];

  const uniqueStates = Array.from(
    new Set(
      (filteredInvoices.length > 0
        ? filteredInvoices
        : invoicedatas?.data?.invoices || []
      ).map((invoice) => {
        const state = invoice.state || "N/A";
        return normalizeState(state);
      })
    )
  )
    .filter(Boolean)
    .map((normalizedState) => {
      const invoice = (filteredInvoices.length > 0
        ? filteredInvoices
        : invoicedatas?.data?.invoices || []
      ).find((inv) => normalizeState(inv.state || "N/A") === normalizedState);
      
      const originalState = invoice?.state || normalizedState;
      return { value: normalizedState, displayName: originalState };
    })
    .sort((a, b) => a.displayName.localeCompare(b.displayName));

  const uniqueDistricts = Array.from(
    new Set(
      (filteredInvoices.length > 0
        ? filteredInvoices
        : invoicedatas?.data?.invoices || []
      ).map((invoice) => invoice.district || "N/A")
    )
  ).filter(Boolean);

  const uniqueVCs = Array.from(
    new Set(
      (filteredInvoices.length > 0
        ? filteredInvoices
        : invoicedatas?.data?.invoices || []
      ).map((invoice) => invoice.vc_name?.name || invoice.vc_name || "N/A")
    )
  ).filter(Boolean);

  const totalRows = filteredInvoices.filter(inv =>
    !invoiceSearch || (inv.invoice_no || "").toLowerCase() === invoiceSearch.toLowerCase().trim()
  ).length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
  };
  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const currentPageInvoices = filteredInvoices
    .filter(inv => !invoiceSearch || (inv.invoice_no || "").toLowerCase() === invoiceSearch.toLowerCase().trim())
    .slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const paginatedRows = currentPageInvoices
    .map((invoice, index) => [
      <Checkbox 
        checked={selectedInvoiceIds.includes(invoice._id)}
        onChange={() => handleSelectInvoice(invoice._id)}
        size="small"
      />,
      (currentPage - 1) * pageSize + index + 1,
      invoice.invoice_date
        ? new Date(invoice.invoice_date).toLocaleDateString("en-IN")
        : "N/A",
      invoice.invoice_no || "N/A",
      invoice.state || "N/A",
      invoice.district || "N/A",
      invoice.vc_name?.name || invoice.vc_name || "N/A",
      invoice.bill_period || "N/A",
      invoice.bill_type || "N/A",
      formatCurrency(invoice.invoice_amount),
      formatCurrency(invoice.tds),
      formatCurrency(invoice.amount_deducted),
      formatCurrency(invoice.payment_received),
      formatDate(invoice.recived_date),
      invoice.submitted_date || "N/A",
      invoice.submitted_to || "N/A",
      invoice.contact_no || "N/A",
      invoice.courier_company || "N/A",
      invoice.consignment_no || "N/A",
      formatCurrency(invoice.balance),
      invoice.aging || "N/A",
      invoice.created_by?.email || " ",
      invoice.remarks || "N/A",
      <div style={{ display: "flex", gap: "8px", flexWrap: "nowrap", alignItems: "center" }}>
        {invoice.photo && invoice.photo !== "none" && invoice.photo !== "null" && invoice.photo !== "undefined" && invoice.photo !== "N/A" && invoice.photo.trim() !== "" ? (
          <Button
            variant="contained"
            color="info"
            onClick={() => handleViewImage(invoice.photo)}
            style={{ minWidth: "60px", padding: "4px 8px", fontSize: "12px" }}
          >
            View
          </Button>
        ) : (
          canUploadInvoice() && (
            <Button
              variant="contained"
              color="success"
              onClick={() => handleUploadPdf(invoice.invoice_no)}
              style={{ minWidth: "60px", padding: "4px 8px", fontSize: "12px" }}
            >
              Upload
            </Button>
          )
        )}
        {canEditDelete() && (
          <>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => handleEdit(invoice)}
              style={{ minWidth: "60px", padding: "4px 8px", fontSize: "12px" }}
            >
              Edit
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDelete(invoice.invoice_no)}
              style={{ minWidth: "60px", padding: "4px 8px", fontSize: "12px" }}
            >
              Delete
            </Button>
          </>
        )}
      </div>,
    ]);

  if (invoiceIsLoading || uploadLoading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "80vh", gap: "12px" }}>
        <div style={{
          width: "48px",
          height: "48px",
          border: "4px solid #eef2f6",
          borderTop: "4px solid #3b82f6",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
        }} />
        <span style={{ fontFamily: "Poppins, sans-serif", fontSize: "16px", color: "#475569" }}>Loading Dashboard...</span>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Styles
  const containerStyle = {
    backgroundColor: "#eaedf2",
    padding: isMobile ? "12px" : "24px",
    minHeight: "100vh",
    width: "100%",
    overflowX: "hidden",
    fontFamily: "Poppins, sans-serif",
  };

  const wrapperStyle = {
    maxWidth: isMobile ? "100%" : "1200px",
    margin: "0 auto",
    width: "100%",
  };

  const summaryGridStyle = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(6, 1fr)",
    gap: "14px",
    marginBottom: "24px",
  };

  const cardStyle = {
    padding: "20px 16px",
    borderRadius: "12px",
    color: "#ffffff",
    textAlign: "center",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    cursor: "pointer",
    position: "relative",
    overflow: "hidden",
    minHeight: "110px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  };

  const tableSectionStyle = {
    backgroundColor: "#ffffff",
    padding: isMobile ? "12px" : "24px",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  };

  return (
    <div style={containerStyle}>
      <div style={wrapperStyle}>
        
        {/* Overview Section */}
        <div style={summaryGridStyle}>
          {cardsData.map((item) => {
            const isHovered = hoveredCard === item.label;
            return (
              <div
                key={item.label}
                style={{
                  ...cardStyle,
                  backgroundColor: item.color,
                  transform: isHovered ? "translateY(-6px) scale(1.02)" : "translateY(0) scale(1)",
                  boxShadow: isHovered 
                    ? "0 12px 35px rgba(0, 0, 0, 0.25)" 
                    : "0 2px 8px rgba(0, 0, 0, 0.08)",
                  borderColor: isHovered ? "rgba(255, 255, 255, 0.4)" : "rgba(255, 255, 255, 0.15)",
                }}
                onMouseEnter={() => setHoveredCard(item.label)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: isHovered 
                    ? "linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 60%)" 
                    : "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, transparent 50%)",
                  transition: "all 0.3s ease",
                  pointerEvents: "none",
                }} />
                
                <div style={{
                  fontSize: "24px",
                  marginBottom: "4px",
                  position: "relative",
                  zIndex: 1,
                  transform: isHovered ? "scale(1.1)" : "scale(1)",
                  transition: "transform 0.3s ease",
                }}>
                  {item.icon}
                </div>
                
                <h6 style={{
                  margin: 0,
                  fontSize: isMobile ? "12px" : "13px",
                  fontWeight: "500",
                  opacity: 0.9,
                  position: "relative",
                  zIndex: 1,
                  letterSpacing: "0.3px",
                }}>
                  {item.label}
                </h6>
                
                <p style={{
                  fontSize: isMobile ? "16px" : "18px",
                  margin: "6px 0 0",
                  fontWeight: "700",
                  position: "relative",
                  zIndex: 1,
                  textShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  letterSpacing: "0.5px",
                }}>
                  {item.value}
                </p>
                
                <div style={{
                  position: "absolute",
                  bottom: 0,
                  left: isHovered ? "10%" : "50%",
                  right: isHovered ? "10%" : "50%",
                  height: "3px",
                  backgroundColor: "rgba(255,255,255,0.4)",
                  borderRadius: "2px",
                  transition: "all 0.3s ease",
                  opacity: isHovered ? 1 : 0,
                }} />
              </div>
            );
          })}
        </div>

        {/* Average Gap Metrics Section */}
        <div style={{
          backgroundColor: "#ffffff",
          padding: isMobile ? "16px" : "24px",
          borderRadius: "12px",
          border: "1px solid #eef2f6",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
          marginBottom: "24px",
        }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}>
            <h2 style={{
              fontWeight: "700",
              fontSize: isMobile ? "16px" : "18px",
              margin: 0,
              color: "#1a2332",
              letterSpacing: "-0.01em",
            }}>
              Average Gap Metrics
            </h2>
            <button
              onClick={() => {
                setRefreshTrigger(prev => prev + 1);
              }}
              style={{
                backgroundColor: "#3b82f6",
                color: "#ffffff",
                border: "none",
                borderRadius: "6px",
                padding: "8px 16px",
                fontSize: "14px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = "#2563eb"}
              onMouseLeave={(e) => e.target.style.backgroundColor = "#3b82f6"}
              disabled={gapMetricsLoading}
            >
              <span style={{ fontSize: "16px" }}>🔄</span>
              {gapMetricsLoading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
            gap: "20px",
          }}>
            {[
              {
                label: "Salary Due → Paid",
                value: gapMetrics.avgSalaryDueToPaid !== null ? `${gapMetrics.avgSalaryDueToPaid} days` : "N/A",
                color: gapMetrics.avgSalaryDueToPaid === null ? "#9ca3af" : gapMetrics.avgSalaryDueToPaid <= 7 ? "#10b981" : gapMetrics.avgSalaryDueToPaid <= 15 ? "#f59e0b" : "#ef4444",
                icon: "📅",
              },
              {
                label: "Salary Paid → Bill Gen",
                value: gapMetrics.avgSalaryPaidToBillGen !== null ? `${gapMetrics.avgSalaryPaidToBillGen} days` : "N/A",
                color: gapMetrics.avgSalaryPaidToBillGen === null ? "#9ca3af" : gapMetrics.avgSalaryPaidToBillGen <= 7 ? "#10b981" : gapMetrics.avgSalaryPaidToBillGen <= 15 ? "#f59e0b" : "#ef4444",
                icon: "📄",
              },
              {
                label: "Bill Gen → Submission",
                value: gapMetrics.avgBillGenToSubmission !== null ? `${gapMetrics.avgBillGenToSubmission} days` : "N/A",
                color: gapMetrics.avgBillGenToSubmission === null ? "#9ca3af" : gapMetrics.avgBillGenToSubmission <= 7 ? "#10b981" : gapMetrics.avgBillGenToSubmission <= 15 ? "#f59e0b" : "#ef4444",
                icon: "📤",
              },
            ].map((item) => {
              const isHovered = hoveredGapCard === item.label;
              return (
                <div
                  key={item.label}
                  style={{
                    backgroundColor: item.color,
                    color: "#ffffff",
                    padding: "16px",
                    borderRadius: "12px",
                    boxShadow: isHovered 
                      ? "0 12px 35px rgba(0, 0, 0, 0.25)" 
                      : "0 2px 8px rgba(0, 0, 0, 0.08)",
                    border: isHovered ? "1px solid rgba(255, 255, 255, 0.4)" : "1px solid rgba(255, 255, 255, 0.15)",
                    textAlign: "center",
                    position: "relative",
                    minHeight: "120px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    transform: isHovered ? "translateY(-6px) scale(1.02)" : "translateY(0) scale(1)",
                    cursor: "pointer",
                    overflow: "hidden",
                  }}
                  onMouseEnter={() => setHoveredGapCard(item.label)}
                  onMouseLeave={() => setHoveredGapCard(null)}
                >
                  {/* Background gradient overlay on hover */}
                  <div style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: isHovered 
                      ? "linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 60%)" 
                      : "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, transparent 50%)",
                    transition: "all 0.3s ease",
                    pointerEvents: "none",
                  }} />

                  {gapMetricsLoading ? (
                    <>
                      <div style={{
                        width: "40px",
                        height: "40px",
                        border: "4px solid rgba(255, 255, 255, 0.3)",
                        borderTop: "4px solid #ffffff",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                      }} />
                      <p style={{ fontSize: "12px", marginTop: "12px", opacity: 0.9 }}>
                        Loading...
                      </p>
                    </>
                  ) : (
                    <>
                      <div style={{
                        fontSize: "24px",
                        marginBottom: "4px",
                        position: "relative",
                        zIndex: 1,
                        transform: isHovered ? "scale(1.1)" : "scale(1)",
                        transition: "transform 0.3s ease",
                      }}>
                        {item.icon}
                      </div>
                      <h6 style={{
                        margin: 0,
                        fontSize: isMobile ? "12px" : "14px",
                        fontWeight: "500",
                        opacity: 0.9,
                        position: "relative",
                        zIndex: 1,
                        letterSpacing: "0.3px",
                      }}>
                        {item.label}
                      </h6>
                      <p style={{
                        fontSize: isMobile ? "16px" : "20px",
                        margin: "8px 0 0",
                        fontWeight: "bold",
                        position: "relative",
                        zIndex: 1,
                        textShadow: "0 1px 3px rgba(0,0,0,0.1)",
                        letterSpacing: "0.5px",
                      }}>
                        {item.value}
                      </p>
                    </>
                  )}

                  {/* Bottom accent line on hover */}
                  <div style={{
                    position: "absolute",
                    bottom: 0,
                    left: isHovered ? "10%" : "50%",
                    right: isHovered ? "10%" : "50%",
                    height: "3px",
                    backgroundColor: "rgba(255,255,255,0.4)",
                    borderRadius: "2px",
                    transition: "all 0.3s ease",
                    opacity: isHovered ? 1 : 0,
                  }} />
                </div>
              );
            })}
          </div>
          <div style={{
            marginTop: "16px",
            padding: "12px",
            backgroundColor: "#f3f4f6",
            borderRadius: "6px",
            fontSize: "12px",
            color: "#6b7280",
          }}>
            <strong>Note:</strong> Color coding - 🟢 Green: 0-7 days (Good) | 🟡 Yellow: 8-15 days (Moderate) | 🔴 Red: 15+ days (Delayed)
          </div>
        </div>

        {/* Graph Section */}
        <div style={{
          background: "linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)",
          padding: isMobile ? "16px" : "24px",
          borderRadius: "16px",
          border: "1px solid #eef2f6",
          marginBottom: "24px",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
          transition: "box-shadow 0.3s ease",
          position: "relative",
          overflow: "hidden",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 8px 30px rgba(0, 0, 0, 0.06)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.04)";
        }}
        >
          {/* Decorative Elements */}
          <div style={{
            position: "absolute",
            top: "-60px",
            right: "-40px",
            width: "180px",
            height: "180px",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(74, 144, 217, 0.04) 0%, transparent 70%)",
            pointerEvents: "none",
          }} />
          
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "20px",
            flexWrap: isMobile ? "wrap" : "nowrap",
            gap: isMobile ? "12px" : "0",
            position: "relative",
            zIndex: 1,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{
                width: "40px",
                height: "40px",
                borderRadius: "10px",
                background: "linear-gradient(135deg, #4a90d9 0%, #357abd 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "18px",
                color: "#fff",
                flexShrink: 0,
                boxShadow: "0 4px 12px rgba(74, 144, 217, 0.25)",
              }}>
                📊
              </div>
              <div>
                <h2 style={{
                  fontWeight: 700,
                  fontSize: isMobile ? "17px" : "20px",
                  margin: 0,
                  color: "#1a2332",
                  letterSpacing: "-0.02em",
                }}>
                  Invoice Metrics Chart
                </h2>
                <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: "#64748b" }}>
                  Overview of invoice metrics
                </p>
              </div>
            </div>
            
            {/* Legend / Summary Badge */}
            <div style={{ display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap" }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "4px 12px",
                borderRadius: "20px",
                background: "#f1f5f9",
                border: "1px solid #e2e8f0",
              }}>
                <span style={{ width: "10px", height: "10px", borderRadius: "3px", background: "#38a169" }} />
                <span style={{ fontSize: "11px", color: "#475569", fontWeight: 500 }}>
                  Raised: {formatCurrency(overviewData.totalAgingAmount)}
                </span>
              </div>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "4px 12px",
                borderRadius: "20px",
                background: "#f1f5f9",
                border: "1px solid #e2e8f0",
              }}>
                <span style={{ width: "10px", height: "10px", borderRadius: "3px", background: "#3182ce" }} />
                <span style={{ fontSize: "11px", color: "#475569", fontWeight: 500 }}>
                  Collected: {formatCurrency(overviewData.totalAmountCollected)}
                </span>
              </div>
            </div>
          </div>

          {/* Chart Container */}
          <div style={{
            background: "#ffffff",
            borderRadius: "12px",
            padding: "16px",
            border: "1px solid #f0f0f0",
            position: "relative",
            zIndex: 1,
          }}>
            <div style={{ height: isMobile ? "200px" : "350px", position: "relative" }}>
              <canvas ref={chartRef} id="invoiceChart"></canvas>
            </div>
          </div>
        </div>

        {/* Only show Upload Invoice section for Super Admin and account.entry */}
        {canUploadInvoice() && <UploadInvoiceAccordion />}

        {/* Filters Panel - Full Width 2 Row Layout */}
        <div style={{
          background: "#ffffff",
          padding: isMobile ? "16px" : "20px 24px",
          borderRadius: "12px",
          border: "1px solid #eef2f6",
          marginBottom: "24px",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
          transition: "box-shadow 0.3s ease",
        }}>
          
          {/* Filter Title */}
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "16px",
            paddingBottom: "12px",
            borderBottom: "1px solid #f0f0f0",
          }}>
            <span style={{ fontSize: "16px" }}>🔍</span>
            <span style={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#1a2332",
              letterSpacing: "0.02em",
            }}>Filters</span>
            <span style={{
              fontSize: "11px",
              color: "#94a3b8",
              fontWeight: 400,
              marginLeft: "4px",
            }}>
              {filteredInvoices.length > 0 ? `(${filteredInvoices.length} records)` : ""}
            </span>
          </div>

          {/* First Row - Dropdowns */}
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
            gap: "12px",
            marginBottom: "14px",
          }}>
            <Select
              name="state"
              value={filters.state || ""}
              onChange={handleFilterChange}
              displayEmpty
              style={{ 
                width: "100%",
                height: "42px",
                fontSize: "13px",
                backgroundColor: "#ffffff",
                borderRadius: "8px",
              }}
              sx={{
                '& .MuiSelect-select': {
                  padding: '8px 14px',
                  fontSize: '13px',
                  borderRadius: '8px',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d1d9e6',
                  borderRadius: '8px',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4a90d9',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4a90d9',
                  borderWidth: '2px',
                },
              }}
            >
              <MenuItem value="" style={{ fontSize: "13px" }}>📍 State</MenuItem>
              {uniqueStates.map(({ value, displayName }) => (
                <MenuItem key={value} value={value} style={{ fontSize: "13px" }}>
                  {displayName}
                </MenuItem>
              ))}
            </Select>

            <Select
              name="district"
              value={filters.district || ""}
              onChange={handleFilterChange}
              displayEmpty
              style={{ 
                width: "100%",
                height: "42px",
                fontSize: "13px",
                backgroundColor: "#ffffff",
                borderRadius: "8px",
              }}
              sx={{
                '& .MuiSelect-select': {
                  padding: '8px 14px',
                  fontSize: '13px',
                  borderRadius: '8px',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d1d9e6',
                  borderRadius: '8px',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4a90d9',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4a90d9',
                  borderWidth: '2px',
                },
              }}
            >
              <MenuItem value="" style={{ fontSize: "13px" }}>🏙️ District</MenuItem>
              {uniqueDistricts.map((district) => (
                <MenuItem key={district} value={district} style={{ fontSize: "13px" }}>
                  {district}
                </MenuItem>
              ))}
            </Select>

            <Select
              name="vc"
              value={filters.vc || ""}
              onChange={handleFilterChange}
              displayEmpty
              style={{ 
                width: "100%",
                height: "42px",
                fontSize: "13px",
                backgroundColor: "#ffffff",
                borderRadius: "8px",
              }}
              sx={{
                '& .MuiSelect-select': {
                  padding: '8px 14px',
                  fontSize: '13px',
                  borderRadius: '8px',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d1d9e6',
                  borderRadius: '8px',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4a90d9',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4a90d9',
                  borderWidth: '2px',
                },
              }}
            >
              <MenuItem value="" style={{ fontSize: "13px" }}>👤 VC Name</MenuItem>
              {uniqueVCs.map((vc) => (
                <MenuItem key={vc} value={vc} style={{ fontSize: "13px" }}>
                  {vc}
                </MenuItem>
              ))}
            </Select>
          </div>

          {/* Second Row - Date inputs and Buttons */}
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr auto auto",
            gap: "12px",
            alignItems: "center",
          }}>
            <div style={{ position: "relative" }}>
              <span style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#94a3b8",
                fontSize: "14px",
                pointerEvents: "none",
              }}>📅</span>
              <input
                type="date"
                name="fromDate"
                value={filters.fromDate || ""}
                onChange={handleFilterChange}
                placeholder="From Date"
                style={{
                  padding: "8px 14px 8px 38px",
                  border: "1px solid #d1d9e6",
                  borderRadius: "8px",
                  fontSize: "13px",
                  height: "42px",
                  width: "100%",
                  boxSizing: "border-box",
                  backgroundColor: "#ffffff",
                  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                  outline: "none",
                  color: "#1a2332",
                  cursor: "pointer",
                }}
              />
            </div>

            <div style={{ position: "relative" }}>
              <span style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#94a3b8",
                fontSize: "14px",
                pointerEvents: "none",
              }}>📅</span>
              <input
                type="date"
                name="toDate"
                value={filters.toDate || ""}
                onChange={handleFilterChange}
                placeholder="To Date"
                style={{
                  padding: "8px 14px 8px 38px",
                  border: "1px solid #d1d9e6",
                  borderRadius: "8px",
                  fontSize: "13px",
                  height: "42px",
                  width: "100%",
                  boxSizing: "border-box",
                  backgroundColor: "#ffffff",
                  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                  outline: "none",
                  color: "#1a2332",
                  cursor: "pointer",
                }}
              />
            </div>

            <button
              onClick={handleApplyFilters}
              style={{
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                padding: "0 28px",
                fontSize: "13px",
                fontWeight: 600,
                height: "42px",
                whiteSpace: "nowrap",
                cursor: "pointer",
                transition: "all 0.25s ease",
                boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
                gridColumn: isMobile ? "span 1" : "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <span>✓</span> Apply
            </button>

            <button
              onClick={handleResetFilters}
              style={{
                background: "#ffffff",
                color: "#ef4444",
                border: "1.5px solid #ef4444",
                borderRadius: "8px",
                padding: "0 28px",
                fontSize: "13px",
                fontWeight: 600,
                height: "42px",
                whiteSpace: "nowrap",
                cursor: "pointer",
                transition: "all 0.25s ease",
                gridColumn: isMobile ? "span 1" : "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <span>⟳</span> Reset
            </button>
          </div>
        </div>

        {/* Table Section */}
        <div style={tableSectionStyle}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}>
            <h2 style={{
              fontWeight: "600",
              fontSize: isMobile ? "18px" : "22px",
              margin: 0,
              color: "#1a2332",
              letterSpacing: "-0.01em",
            }}>
              Invoice Records
            </h2>
          </div>

          {/* Search and Actions Bar - All in One Row */}
          <div style={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
            padding: "12px 16px",
            background: "#f8fafc",
            borderRadius: "8px",
            border: "1px solid #eef2f6",
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}>
            
            {/* Left Side - Search */}
            <div style={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
              flex: "1",
              minWidth: isMobile ? "100%" : "auto",
            }}>
              <div style={{
                position: "relative",
                flex: "1",
                maxWidth: isMobile ? "100%" : "380px",
              }}>
                <span style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#94a3b8",
                  fontSize: "14px",
                }}>🔍</span>
                <input
                  type="text"
                  placeholder="Search by Invoice Number..."
                  value={invoiceSearchInput}
                  onChange={(e) => setInvoiceSearchInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleInvoiceSearch();
                    }
                  }}
                  style={{
                    width: "100%",
                    padding: "8px 12px 8px 36px",
                    border: "1px solid #d1d9e6",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    background: "#ffffff",
                    transition: "border-color 0.2s, box-shadow 0.2s",
                    outline: "none",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#4a90d9";
                    e.target.style.boxShadow = "0 0 0 3px rgba(74, 144, 217, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#d1d9e6";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>
              <button
                onClick={handleInvoiceSearch}
                style={{
                  background: "#4a90d9",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 18px",
                  fontSize: "13px",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => e.target.style.background = "#357abd"}
                onMouseLeave={(e) => e.target.style.background = "#4a90d9"}
              >
                Search
              </button>
              {invoiceSearch && (
                <button
                  onClick={() => {
                    setInvoiceSearch("");
                    setInvoiceSearchInput("");
                    if (invoicedatas?.data?.invoices) {
                      setFilteredInvoices(invoicedatas.data.invoices);
                    }
                  }}
                  style={{
                    background: "transparent",
                    color: "#ef4444",
                    border: "1px solid #ef4444",
                    borderRadius: "6px",
                    padding: "7px 16px",
                    fontSize: "13px",
                    fontWeight: "500",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#ef4444";
                    e.target.style.color = "#fff";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "transparent";
                    e.target.style.color = "#ef4444";
                  }}
                >
                  ✕ Clear
                </button>
              )}
            </div>

            {/* Right Side - Action Buttons */}
            <div style={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
              flexShrink: 0,
              flexWrap: isMobile ? "wrap" : "nowrap",
            }}>
              {user?.role === "superadmin" && selectedInvoiceIds.length > 0 && (
                <button
                  onClick={() => handleBulkDelete('selected')}
                  style={{
                    background: "#ef4444",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    padding: "8px 16px",
                    fontSize: "13px",
                    fontWeight: "500",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    transition: "all 0.2s ease",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={(e) => e.target.style.background = "#dc2626"}
                  onMouseLeave={(e) => e.target.style.background = "#ef4444"}
                >
                  <span>🗑️</span>
                  Delete Selected ({selectedInvoiceIds.length})
                </button>
              )}
            </div>
          </div>

          {filteredInvoices.length === 0 ? (
            <div style={{ padding: "40px 0", textAlign: "center", color: "#94a3b8" }}>
              <span style={{ fontSize: "40px" }}>📋</span>
              <p style={{ fontSize: "14px", marginTop: "8px" }}>No invoices found</p>
            </div>
          ) : (
            <>
              {/* Table Container */}
              <TableContainer
                component={Paper}
                style={{
                  maxWidth: "100%",
                  overflowX: "auto",
                  overflowY: "auto",
                  maxHeight: "1200px",
                  borderRadius: "10px",
                  border: "1px solid #eef2f6",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
                }}
              >
                <Table style={{ minWidth: 650 }} aria-label="invoice table" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {headers.map((header, index) => (
                        <TableCell
                          key={header}
                          className={index === headers.length - 1 ? "sticky-action-cell" : ""}
                          style={{
                            fontWeight: 600,
                            fontSize: "12px",
                            padding: "12px 16px",
                            cursor: index !== 0 && index !== headers.length - 1 ? "pointer" : "default",
                            backgroundColor: "#f8fafc",
                            borderBottom: "2px solid #eef2f6",
                            position: "sticky",
                            top: 0,
                            zIndex: index === 0 ? 3 : index === headers.length - 1 ? 2 : 1,
                            color: "#475569",
                            textTransform: "uppercase",
                            letterSpacing: "0.03em",
                            whiteSpace: "nowrap",
                            right: index === headers.length - 1 ? 0 : "auto",
                            boxShadow: index === headers.length - 1 ? "-2px 0 5px rgba(0,0,0,0.02)" : "none",
                            ...(index === headers.length - 1 && { width: "240px", minWidth: "240px" }),
                          }}
                          onClick={() =>
                            index !== 0 && index !== headers.length - 1 &&
                            handleSort(
                              index === 1 ? "sno" :
                              index === 2 ? "invoice_date" :
                              index === 3 ? "invoice_no" :
                              index === 4 ? "state" :
                              index === 5 ? "district" :
                              index === 6 ? "vc_name" :
                              index === 7 ? "bill_period" :
                              index === 8 ? "bill_type" :
                              index === 9 ? "invoice_amount" :
                              index === 10 ? "tds" :
                              index === 11 ? "amount_deducted" :
                              index === 12 ? "payment_received" :
                              index === 13 ? "recived_date" :
                              index === 14 ? "submitted_date" :
                              index === 15 ? "submitted_to" :
                              index === 16 ? "contact_no" :
                              index === 17 ? "courier_company" :
                              index === 18 ? "consignment_no" :
                              index === 19 ? "balance" :
                              index === 20 ? "aging" :
                              index === 21 ? "created_by" : "remarks",
                              index
                            )
                          }
                          onMouseEnter={(e) => {
                            if (index !== 0 && index !== headers.length - 1) {
                              e.target.style.color = "#1a2332";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (index !== 0 && index !== headers.length - 1) {
                              e.target.style.color = "#475569";
                            }
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            {index === 0 ? (
                              <Checkbox
                                checked={currentPageInvoices.length > 0 && selectedInvoiceIds.length === currentPageInvoices.length && currentPageInvoices.every(inv => selectedInvoiceIds.includes(inv._id))}
                                indeterminate={selectedInvoiceIds.length > 0 && selectedInvoiceIds.length < currentPageInvoices.length}
                                onChange={handleSelectAll}
                                size="small"
                              />
                            ) : (
                              <>
                                {header}
                                {sortConfig.key === (
                                  index === 1 ? "sno" :
                                  index === 2 ? "invoice_date" :
                                  index === 3 ? "invoice_no" :
                                  index === 4 ? "state" :
                                  index === 5 ? "district" :
                                  index === 6 ? "vc_name" :
                                  index === 7 ? "bill_period" :
                                  index === 8 ? "bill_type" :
                                  index === 9 ? "invoice_amount" :
                                  index === 10 ? "tds" :
                                  index === 11 ? "amount_deducted" :
                                  index === 12 ? "payment_received" :
                                  index === 13 ? "recived_date" :
                                  index === 14 ? "submitted_date" :
                                  index === 15 ? "submitted_to" :
                                  index === 16 ? "contact_no" :
                                  index === 17 ? "courier_company" :
                                  index === 18 ? "consignment_no" :
                                  index === 19 ? "balance" :
                                  index === 20 ? "aging" :
                                  index === 21 ? "created_by" : "remarks"
                                ) && (
                                  <span style={{ fontSize: "10px", color: "#4a90d9" }}>
                                    {sortConfig.direction === "asc" ? "↑" : "↓"}
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRows.map((row, rowIndex) => (
                      <TableRow
                        key={rowIndex}
                        style={{ transition: "background-color 0.15s ease" }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f8fafc";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        {row.map((data, index) => (
                          <TableCell
                            key={index}
                            className={index === row.length - 1 ? "sticky-action-cell" : ""}
                            style={{
                              padding: "10px 16px",
                              fontSize: "13px",
                              borderBottom: "1px solid #f0f0f0",
                              color: index >= 9 && index <= 12 || index === 19 ? "#1a2332" : "#475569",
                              fontWeight: index >= 9 && index <= 12 || index === 19 ? 500 : 400,
                              ...(index !== row.length - 1 ? {
                                maxWidth: "200px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              } : {
                                whiteSpace: "nowrap",
                                width: "240px",
                                minWidth: "240px",
                              }),
                              position: index === row.length - 1 ? "sticky" : "static",
                              right: index === row.length - 1 ? 0 : "auto",
                              backgroundColor: index === row.length - 1 ? "#ffffff" : "transparent",
                              boxShadow: index === row.length - 1 ? "-2px 0 5px rgba(0,0,0,0.02)" : "none",
                              zIndex: index === row.length - 1 ? 1 : "auto",
                            }}
                          >
                            {data}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Custom Pagination Toolbar exactly matching SalaryPayment.js */}
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 16px",
                  borderTop: "1px solid #eef2f6",
                  background: "#fafbfc",
                  borderRadius: "0 0 10px 10px",
                  flexWrap: isMobile ? "wrap" : "nowrap",
                  gap: isMobile ? "8px" : "0",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontSize: "13px", color: "#64748b" }}>Rows per page:</span>
                    <select
                      value={pageSize}
                      onChange={handlePageSizeChange}
                      style={{
                        padding: "4px 8px",
                        border: "1px solid #d1d9e6",
                        borderRadius: "4px",
                        fontSize: "13px",
                        background: "#fff",
                        outline: "none",
                        cursor: "pointer",
                      }}
                    >
                      {[10, 20, 50, 100].map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <span style={{ fontSize: "13px", color: "#64748b" }}>
                      {(currentPage - 1) * pageSize + 1}-
                      {Math.min(currentPage * pageSize, totalRows)} of {totalRows}
                    </span>
                    <div style={{ display: "flex", gap: "4px" }}>
                      <button
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                        style={{
                          padding: "4px 8px",
                          border: "1px solid #d1d9e6",
                          borderRadius: "4px",
                          background: "#fff",
                          cursor: currentPage === 1 ? "not-allowed" : "pointer",
                          opacity: currentPage === 1 ? 0.4 : 1,
                          color: "#475569",
                          fontSize: "12px",
                          transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          if (currentPage !== 1) e.target.style.background = "#f1f5f9";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "#fff";
                        }}
                      >
                        ⟪
                      </button>
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        style={{
                          padding: "4px 8px",
                          border: "1px solid #d1d9e6",
                          borderRadius: "4px",
                          background: "#fff",
                          cursor: currentPage === 1 ? "not-allowed" : "pointer",
                          opacity: currentPage === 1 ? 0.4 : 1,
                          color: "#475569",
                          fontSize: "12px",
                          transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          if (currentPage !== 1) e.target.style.background = "#f1f5f9";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "#fff";
                        }}
                      >
                        ⟨
                      </button>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        style={{
                          padding: "4px 8px",
                          border: "1px solid #d1d9e6",
                          borderRadius: "4px",
                          background: "#fff",
                          cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                          opacity: currentPage === totalPages ? 0.4 : 1,
                          color: "#475569",
                          fontSize: "12px",
                          transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          if (currentPage < totalPages) e.target.style.background = "#f1f5f9";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "#fff";
                        }}
                      >
                        ⟩
                      </button>
                      <button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        style={{
                          padding: "4px 8px",
                          border: "1px solid #d1d9e6",
                          borderRadius: "4px",
                          background: "#fff",
                          cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                          opacity: currentPage === totalPages ? 0.4 : 1,
                          color: "#475569",
                          fontSize: "12px",
                          transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          if (currentPage < totalPages) e.target.style.background = "#f1f5f9";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "#fff";
                        }}
                      >
                        ⟫
                      </button>
                    </div>
                  </div>
                </div>
              </TableContainer>
            </>
          )}
        </div>

        {/* Export Data Section - Premium Design matching SalaryPayment.js */}
        <div style={{
          background: "linear-gradient(145deg, #ffffff 0%, #fafbfc 100%)",
          padding: isMobile ? "20px" : "28px",
          marginTop: "24px",
          borderRadius: "20px",
          border: "1px solid rgba(238, 242, 246, 0.8)",
          marginBottom: "24px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.02), 0 1px 3px rgba(0, 0, 0, 0.03)",
          position: "relative",
          overflow: "hidden",
          backdropFilter: "blur(10px)",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 12px 48px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)";
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.borderColor = "#d1d9e6";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 20px rgba(0, 0, 0, 0.02), 0 1px 3px rgba(0, 0, 0, 0.03)";
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.borderColor = "rgba(238, 242, 246, 0.8)";
        }}
        >
          {/* Animated Gradient Background */}
          <div style={{
            position: "absolute",
            top: "-50%",
            right: "-30%",
            width: "60%",
            height: "100%",
            background: "radial-gradient(circle, rgba(74, 144, 217, 0.03) 0%, transparent 70%)",
            pointerEvents: "none",
            animation: "float 6s ease-in-out infinite",
          }} />

          {/* Header */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "24px",
            paddingBottom: "20px",
            borderBottom: "1px solid rgba(238, 242, 246, 0.6)",
            position: "relative",
            zIndex: 1,
            flexWrap: isMobile ? "wrap" : "nowrap",
            gap: isMobile ? "12px" : "0",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <div style={{
                width: "52px",
                height: "52px",
                borderRadius: "16px",
                background: "linear-gradient(135deg, #4a90d9 0%, #357abd 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "22px",
                color: "#fff",
                flexShrink: 0,
                boxShadow: "0 8px 24px rgba(74, 144, 217, 0.25)",
                position: "relative",
                animation: "pulse-glow 3s ease-in-out infinite",
              }}>
                💾
                <div style={{
                  position: "absolute",
                  top: "-4px",
                  right: "-4px",
                  width: "16px",
                  height: "16px",
                  borderRadius: "50%",
                  background: "#10b981",
                  border: "2px solid #fff",
                  animation: "blink 2s ease-in-out infinite",
                }} />
              </div>
              <div>
                <h3 style={{
                  fontWeight: 700,
                  fontSize: isMobile ? "17px" : "20px",
                  margin: 0,
                  color: "#1a2332",
                  letterSpacing: "-0.02em",
                  background: "linear-gradient(135deg, #1a2332 0%, #4a90d9 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}>
                  Export Data
                </h3>
                <p style={{ margin: "2px 0 0 0", fontSize: "13px", color: "#64748b", display: "flex", alignItems: "center", gap: "6px" }}>
                  <span>📋</span>
                  {filteredInvoices.length} records · 
                  <span>💰</span>
                  {formatCurrency(overviewData.totalAgingAmount)}
                </p>
              </div>
            </div>
            
            {/* Quick Stats Badge */}
            <div style={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
              padding: "6px 16px",
              borderRadius: "100px",
              background: "rgba(255, 255, 255, 0.8)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(238, 242, 246, 0.6)",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.02)",
            }}>
              <span style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: "#10b981",
                display: "inline-block",
                animation: "pulse 2s ease-in-out infinite",
              }} />
              <span style={{ fontSize: "12px", color: "#475569", fontWeight: 500 }}>
                Ready to export
              </span>
            </div>
          </div>

          {/* Export Options - Modern Card Grid */}
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)",
            gap: "16px",
            position: "relative",
            zIndex: 1,
          }}>
            
            {/* CSV Card */}
            <div style={{
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              borderRadius: "16px",
              padding: "20px 16px",
              border: "1px solid rgba(238, 242, 246, 0.6)",
              textAlign: "center",
              transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.02)",
              position: "relative",
              overflow: "hidden",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
              e.currentTarget.style.boxShadow = "0 16px 40px rgba(74, 144, 217, 0.15)";
              e.currentTarget.style.borderColor = "#4a90d9";
              e.currentTarget.style.background = "#ffffff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0) scale(1)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.02)";
              e.currentTarget.style.borderColor = "rgba(238, 242, 246, 0.6)";
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)";
            }}
            onClick={downloadTableCSV}
            >
              <div style={{
                width: "56px",
                height: "56px",
                borderRadius: "16px",
                background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 12px",
                fontSize: "24px",
                transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.15) rotate(-5deg)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1) rotate(0deg)";
              }}
              >
                📄
              </div>
              <h4 style={{ fontSize: "14px", fontWeight: 700, color: "#1a2332", margin: "0 0 4px 0" }}>CSV</h4>
              <p style={{ fontSize: "12px", color: "#94a3b8", margin: 0, lineHeight: 1.4 }}>Spreadsheet format</p>
              <div style={{
                marginTop: "14px",
                padding: "6px 16px",
                borderRadius: "100px",
                background: "rgba(74, 144, 217, 0.08)",
                fontSize: "11px",
                color: "#4a90d9",
                fontWeight: 600,
                display: "inline-block",
              }}>
                ⬇ Download
              </div>
            </div>

            {/* PDF Card */}
            <div style={{
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              borderRadius: "16px",
              padding: "20px 16px",
              border: "1px solid rgba(238, 242, 246, 0.6)",
              textAlign: "center",
              transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.02)",
              position: "relative",
              overflow: "hidden",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
              e.currentTarget.style.boxShadow = "0 16px 40px rgba(239, 68, 68, 0.12)";
              e.currentTarget.style.borderColor = "#ef4444";
              e.currentTarget.style.background = "#ffffff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0) scale(1)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.02)";
              e.currentTarget.style.borderColor = "rgba(238, 242, 246, 0.6)";
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)";
            }}
            onClick={downloadPDF}
            >
              <div style={{
                width: "56px",
                height: "56px",
                borderRadius: "16px",
                background: "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 12px",
                fontSize: "24px",
                transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.15) rotate(-5deg)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1) rotate(0deg)";
              }}
              >
                📑
              </div>
              <h4 style={{ fontSize: "14px", fontWeight: 700, color: "#1a2332", margin: "0 0 4px 0" }}>PDF</h4>
              <p style={{ fontSize: "12px", color: "#94a3b8", margin: 0, lineHeight: 1.4 }}>Document format</p>
              <div style={{
                marginTop: "14px",
                padding: "6px 16px",
                borderRadius: "100px",
                background: "rgba(239, 68, 68, 0.08)",
                fontSize: "11px",
                color: "#ef4444",
                fontWeight: 600,
                display: "inline-block",
              }}>
                ⬇ Download
              </div>
            </div>

            {/* Excel Card */}
            <div style={{
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              borderRadius: "16px",
              padding: "20px 16px",
              border: "1px solid rgba(238, 242, 246, 0.6)",
              textAlign: "center",
              transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.02)",
              position: "relative",
              overflow: "hidden",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
              e.currentTarget.style.boxShadow = "0 16px 40px rgba(16, 185, 129, 0.12)";
              e.currentTarget.style.borderColor = "#10b981";
              e.currentTarget.style.background = "#ffffff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0) scale(1)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.02)";
              e.currentTarget.style.borderColor = "rgba(238, 242, 246, 0.6)";
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)";
            }}
            onClick={downloadTableCSV}
            >
              <div style={{
                width: "56px",
                height: "56px",
                borderRadius: "16px",
                background: "linear-gradient(135deg, #f0fdf4 0%, #bbf7d0 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 12px",
                fontSize: "24px",
                transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.15) rotate(-5deg)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1) rotate(0deg)";
              }}
              >
                📊
              </div>
              <h4 style={{ fontSize: "14px", fontWeight: 700, color: "#1a2332", margin: "0 0 4px 0" }}>Excel</h4>
              <p style={{ fontSize: "12px", color: "#94a3b8", margin: 0, lineHeight: 1.4 }}>Spreadsheet format</p>
              <div style={{
                marginTop: "14px",
                padding: "6px 16px",
                borderRadius: "100px",
                background: "rgba(16, 185, 129, 0.08)",
                fontSize: "11px",
                color: "#10b981",
                fontWeight: 600,
                display: "inline-block",
              }}>
                ⬇ Download
              </div>
            </div>

            {/* Print Card */}
            <div style={{
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              borderRadius: "16px",
              padding: "20px 16px",
              border: "1px solid rgba(238, 242, 246, 0.6)",
              textAlign: "center",
              transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.02)",
              position: "relative",
              overflow: "hidden",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
              e.currentTarget.style.boxShadow = "0 16px 40px rgba(139, 92, 246, 0.12)";
              e.currentTarget.style.borderColor = "#8b5cf6";
              e.currentTarget.style.background = "#ffffff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0) scale(1)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.02)";
              e.currentTarget.style.borderColor = "rgba(238, 242, 246, 0.6)";
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)";
            }}
            onClick={handlePrint}
            >
              <div style={{
                width: "56px",
                height: "56px",
                borderRadius: "16px",
                background: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 12px",
                fontSize: "24px",
                transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.15) rotate(-5deg)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1) rotate(0deg)";
              }}
              >
                🖨️
              </div>
              <h4 style={{ fontSize: "14px", fontWeight: 700, color: "#1a2332", margin: "0 0 4px 0" }}>Print</h4>
              <p style={{ fontSize: "12px", color: "#94a3b8", margin: 0, lineHeight: 1.4 }}>Direct printing</p>
              <div style={{
                marginTop: "14px",
                padding: "6px 16px",
                borderRadius: "100px",
                background: "rgba(139, 92, 246, 0.08)",
                fontSize: "11px",
                color: "#8b5cf6",
                fontWeight: 600,
                display: "inline-block",
              }}>
                🖨 Print
              </div>
            </div>
          </div>

          {/* Bottom stats summary */}
          <div style={{
            marginTop: "20px",
            paddingTop: "18px",
            borderTop: "1px solid rgba(238, 242, 246, 0.6)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: isMobile ? "wrap" : "nowrap",
            gap: isMobile ? "10px" : "0",
            position: "relative",
            zIndex: 1,
          }}>
            <div style={{ display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap" }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "4px 12px",
                borderRadius: "100px",
                background: "rgba(255, 255, 255, 0.6)",
                border: "1px solid rgba(238, 242, 246, 0.6)",
              }}>
                <span>📋</span>
                <span style={{ fontSize: "12px", color: "#475569", fontWeight: 500 }}>
                  {filteredInvoices.length} invoices
                </span>
              </div>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "4px 12px",
                borderRadius: "100px",
                background: "rgba(255, 255, 255, 0.6)",
                border: "1px solid rgba(238, 242, 246, 0.6)",
              }}>
                <span>💰</span>
                <span style={{ fontSize: "12px", color: "#475569", fontWeight: 600 }}>
                  {formatCurrency(overviewData.totalAgingAmount)}
                </span>
              </div>
            </div>
            <div style={{ fontSize: "11px", color: "#94a3b8", display: "flex", alignItems: "center", gap: "4px" }}>
              <span>⚡</span> Quick export dashboard
            </div>
          </div>

          <style>
            {`
              @keyframes float {
                0%, 100% { transform: translateY(0px) rotate(0deg); }
                50% { transform: translateY(-10px) rotate(2deg); }
              }
              @keyframes pulse-glow {
                0%, 100% { box-shadow: 0 8px 24px rgba(74, 144, 217, 0.25); }
                50% { box-shadow: 0 8px 40px rgba(74, 144, 217, 0.35); }
              }
              @keyframes blink {
                0%, 100% { opacity: 1; transform: scale(1); }
                50% { opacity: 0.5; transform: scale(0.9); }
              }
              @keyframes pulse {
                0%, 100% { opacity: 1; transform: scale(1); }
                50% { opacity: 0.5; transform: scale(0.8); }
              }
            `}
          </style>
        </div>

        {/* Bulk Import section styled matching SalaryPayment.js */}
        {canUploadInvoice() && (
          <div style={{
            background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
            padding: isMobile ? "20px" : "28px",
            borderRadius: "16px",
            border: "1px solid #eef2f6",
            marginTop: "24px",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
            position: "relative",
            overflow: "hidden",
            transition: "all 0.3s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = "0 8px 30px rgba(0, 0, 0, 0.06)";
            e.currentTarget.style.borderColor = "#d1d9e6";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.04)";
            e.currentTarget.style.borderColor = "#eef2f6";
          }}
          >
            {/* Decorative Background Element */}
            <div style={{
              position: "absolute",
              top: "-50px",
              right: "-50px",
              width: "200px",
              height: "200px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, rgba(74, 144, 217, 0.05) 0%, rgba(16, 185, 129, 0.05) 100%)",
              pointerEvents: "none",
            }} />

            {/* Header */}
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "24px",
              paddingBottom: "20px",
              borderBottom: "1px solid #f0f0f0",
              position: "relative",
              zIndex: 1,
              flexWrap: isMobile ? "wrap" : "nowrap",
              gap: isMobile ? "12px" : "0",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                <div style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #4a90d9 0%, #357abd 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                  color: "#fff",
                  flexShrink: 0,
                  boxShadow: "0 4px 12px rgba(74, 144, 217, 0.3)",
                }}>
                  📤
                </div>
                <div>
                  <h2 style={{ fontWeight: 700, fontSize: isMobile ? "18px" : "20px", margin: 0, color: "#1a2332", letterSpacing: "-0.02em" }}>
                    Bulk Import Invoices
                  </h2>
                  <p style={{ margin: "2px 0 0 0", fontSize: "13px", color: "#64748b" }}>
                    Import multiple invoices in one go
                  </p>
                </div>
              </div>
              
              {/* Status Badge */}
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 14px",
                borderRadius: "20px",
                background: selectedFile ? "#f0fdf4" : "#f1f5f9",
                border: `1px solid ${selectedFile ? "#bbf7d0" : "#e2e8f0"}`,
              }}>
                <span style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  background: selectedFile ? "#10b981" : "#94a3b8",
                  animation: selectedFile ? "pulse 2s infinite" : "none",
                }} />
                <span style={{ fontSize: "12px", fontWeight: 500, color: selectedFile ? "#166534" : "#64748b" }}>
                  {selectedFile ? "File Ready" : "No File"}
                </span>
              </div>
            </div>

            {/* Main Content - Three Column Layout */}
            <div style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1.5fr 1fr",
              gap: "20px",
              position: "relative",
              zIndex: 1,
            }}>
              
              {/* Step 1: Download Template */}
              <div style={{
                background: "#ffffff",
                borderRadius: "12px",
                padding: "20px",
                border: "1px solid #eef2f6",
                textAlign: "center",
                transition: "all 0.3s ease",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.02)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 8px 25px rgba(0, 0, 0, 0.06)";
                e.currentTarget.style.borderColor = "#d1d9e6";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.02)";
                e.currentTarget.style.borderColor = "#eef2f6";
              }}
              >
                <div style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "14px",
                  background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px",
                  fontSize: "24px",
                }}>
                  📄
                </div>
                <h3 style={{ fontSize: "14px", fontWeight: 600, color: "#1a2332", margin: "0 0 4px 0" }}>
                  Step 1: Download Template
                </h3>
                <p style={{ fontSize: "12px", color: "#64748b", margin: "0 0 16px 0", lineHeight: 1.5 }}>
                  Get the CSV template with correct headers
                </p>
                <button
                  onClick={downloadCsvTemplate}
                  style={{
                    background: "linear-gradient(135deg, #4a90d9 0%, #357abd 100%)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "8px",
                    padding: "10px 20px",
                    fontSize: "13px",
                    fontWeight: 500,
                    cursor: "pointer",
                    width: "100%",
                    transition: "all 0.25s ease",
                    boxShadow: "0 2px 8px rgba(74, 144, 217, 0.25)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow = "0 4px 14px rgba(74, 144, 217, 0.35)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "0 2px 8px rgba(74, 144, 217, 0.25)";
                  }}
                >
                  <span>⬇️</span> Download Template
                </button>
              </div>

              {/* Step 2: Upload File */}
              <div style={{
                background: "#ffffff",
                borderRadius: "12px",
                padding: "20px",
                border: selectedFile ? "2px solid #10b981" : "2px dashed #d1d9e6",
                textAlign: "center",
                transition: "all 0.3s ease",
                cursor: "pointer",
                position: "relative",
                boxShadow: selectedFile ? "0 0 0 4px rgba(16, 185, 129, 0.1)" : "none",
              }}
              onMouseEnter={(e) => {
                if (!selectedFile) {
                  e.currentTarget.style.borderColor = "#4a90d9";
                  e.currentTarget.style.background = "#fafbfc";
                }
              }}
              onMouseLeave={(e) => {
                if (!selectedFile) {
                  e.currentTarget.style.borderColor = "#d1d9e6";
                  e.currentTarget.style.background = "#ffffff";
                }
              }}
              >
                <input
                  id="file-upload-new-invoice"
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    opacity: 0,
                    cursor: "pointer",
                  }}
                />
                <div style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "14px",
                  background: selectedFile 
                    ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)" 
                    : "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px",
                  fontSize: "24px",
                  transition: "all 0.3s ease",
                }}>
                  {selectedFile ? "✅" : "📤"}
                </div>
                
                <h3 style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: selectedFile ? "#166534" : "#1a2332",
                  margin: "0 0 4px 0",
                }}>
                  Step 2: {selectedFile ? "File Selected" : "Upload CSV"}
                </h3>
                
                {selectedFile ? (
                  <>
                    <p style={{ fontSize: "13px", fontWeight: 500, color: "#065f46", margin: "0 0 4px 0" }}>
                      {selectedFile.name}
                    </p>
                    <p style={{ fontSize: "11px", color: "#64748b", margin: "0 0 8px 0" }}>
                      {(selectedFile.size / 1024).toFixed(1)} KB • Click to change
                    </p>
                    <div style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: "4px 12px",
                      borderRadius: "12px",
                      background: "#f0fdf4",
                      fontSize: "11px",
                      color: "#166534",
                      fontWeight: 500,
                    }}>
                      ✓ Ready to import
                    </div>
                  </>
                ) : (
                  <>
                    <p style={{ fontSize: "13px", color: "#64748b", margin: "0 0 4px 0" }}>
                      <span style={{ color: "#4a90d9", fontWeight: 500 }}>Click</span> or <span style={{ color: "#4a90d9", fontWeight: 500 }}>drag & drop</span>
                    </p>
                    <p style={{ fontSize: "11px", color: "#94a3b8", margin: "0" }}>
                      Supported: .CSV files only
                    </p>
                  </>
                )}
              </div>

              {/* Step 3: Run Import */}
              <div style={{
                background: "#ffffff",
                borderRadius: "12px",
                padding: "20px",
                border: "1px solid #eef2f6",
                textAlign: "center",
                transition: "all 0.3s ease",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.02)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
              onMouseEnter={(e) => {
                if (selectedFile) {
                  e.currentTarget.style.borderColor = "#d1d9e6";
                  e.currentTarget.style.boxShadow = "0 8px 25px rgba(0, 0, 0, 0.06)";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.02)";
                e.currentTarget.style.borderColor = "#eef2f6";
              }}
              >
                <div style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "14px",
                  background: selectedFile 
                    ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)" 
                    : "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px",
                  fontSize: "24px",
                }}>
                  🚀
                </div>
                <h3 style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: selectedFile ? "#1a2332" : "#94a3b8",
                  margin: "0 0 4px 0",
                }}>
                  Step 3: Import Data
                </h3>
                <p style={{
                  fontSize: "12px",
                  color: selectedFile ? "#64748b" : "#94a3b8",
                  margin: "0 0 16px 0",
                  lineHeight: 1.5,
                }}>
                  {selectedFile ? "Import the uploaded CSV file" : "Upload a file to enable import"}
                </p>
                <button
                  onClick={handleImport}
                  disabled={!selectedFile}
                  style={{
                    background: selectedFile 
                      ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                      : "#e2e8f0",
                    color: selectedFile ? "#fff" : "#94a3b8",
                    border: "none",
                    borderRadius: "8px",
                    padding: "10px 20px",
                    fontSize: "13px",
                    fontWeight: 500,
                    cursor: selectedFile ? "pointer" : "not-allowed",
                    width: "100%",
                    transition: "all 0.25s ease",
                    boxShadow: selectedFile ? "0 2px 8px rgba(16, 185, 129, 0.25)" : "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    opacity: selectedFile ? 1 : 0.5,
                  }}
                  onMouseEnter={(e) => {
                    if (selectedFile) {
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow = "0 4px 14px rgba(16, 185, 129, 0.35)";
                      e.target.style.background = "linear-gradient(135deg, #059669 0%, #047857 100%)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedFile) {
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow = "0 2px 8px rgba(16, 185, 129, 0.25)";
                      e.target.style.background = "linear-gradient(135deg, #10b981 0%, #059669 100%)";
                    }
                  }}
                >
                  <span>⚡</span> 
                  {selectedFile ? "Import Now" : "No File"}
                </button>
              </div>
            </div>

            {/* Step Indicators */}
            <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: "24px", position: "relative", zIndex: 1 }}>
              {[0, 1, 2].map((step) => {
                const isActive = step === 0 || (step === 1 && selectedFile) || (step === 2 && selectedFile);
                const isComplete = step === 0 || (step === 1 && selectedFile);
                return (
                  <div key={step} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      background: isComplete 
                        ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                        : isActive 
                          ? "linear-gradient(135deg, #4a90d9 0%, #357abd 100%)"
                          : "#e2e8f0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: "12px",
                      fontWeight: 600,
                      transition: "all 0.3s ease",
                    }}>
                      {isComplete ? "✓" : step + 1}
                    </div>
                    {step < 2 && (
                      <div style={{
                        width: "40px",
                        height: "2px",
                        background: step === 0 && selectedFile ? "#10b981" : "#e2e8f0",
                        transition: "all 0.3s ease",
                      }} />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Info Note */}
            <div style={{
              marginTop: "20px",
              padding: "14px 18px",
              background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
              borderRadius: "10px",
              border: "1px solid #eef2f6",
              position: "relative",
              zIndex: 1,
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
            }}>
              <span style={{ fontSize: "16px", marginTop: "1px", flexShrink: 0 }}>💡</span>
              <div>
                <p style={{ margin: 0, fontSize: "12px", color: "#475569", lineHeight: 1.6 }}>
                  <strong style={{ color: "#1a2332" }}>Quick Tip:</strong> Make sure your CSV file follows the template format exactly. 
                  Required fields: <span style={{ background: "#e2e8f0", padding: "1px 6px", borderRadius: "4px", fontSize: "11px", color: "#1a2332", fontWeight: 500 }}>Invoice No</span>, 
                  <span style={{ background: "#e2e8f0", padding: "1px 6px", borderRadius: "4px", fontSize: "11px", color: "#1a2332", fontWeight: 500, marginLeft: "4px" }}>Invoice Date</span>, 
                  <span style={{ background: "#e2e8f0", padding: "1px 6px", borderRadius: "4px", fontSize: "11px", color: "#1a2332", fontWeight: 500, marginLeft: "4px" }}>Invoice Amount</span>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Invoice Modal */}
      {editModalOpen && editingInvoice && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: "white",
            padding: "24px",
            borderRadius: "8px",
            width: isMobile ? "90%" : "700px",
            maxHeight: "85vh",
            overflowY: "auto",
          }}>
            <h3 style={{ marginBottom: "20px", fontSize: "20px", fontWeight: "600" }}>
              Edit Invoice: {editingInvoice.invoice_no}
            </h3>
            
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "16px" }}>
              
              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  Invoice Date
                </label>
                <input
                  type="date"
                  value={editingInvoice.invoice_date ? new Date(editingInvoice.invoice_date).toISOString().split('T')[0] : ""}
                  onChange={(e) => setEditingInvoice({...editingInvoice, invoice_date: e.target.value})}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #cbd5e0",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  Invoice No.
                </label>
                <input
                  type="text"
                  value={editingInvoice.invoice_no || ""}
                  onChange={(e) => setEditingInvoice({...editingInvoice, invoice_no: e.target.value})}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #cbd5e0",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  State
                </label>
                <select
                  value={editingInvoice.state || ""}
                  onChange={(e) => setEditingInvoice({...editingInvoice, state: e.target.value, district: "", vc_name: ""})}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #cbd5e0",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                >
                  <option value="">Select State</option>
                  {statelist?.map((state) => (
                    <option key={state._id} value={state.state_name}>
                      {state.state_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  District
                </label>
                <select
                  value={editingInvoice.district || ""}
                  onChange={(e) => setEditingInvoice({...editingInvoice, district: e.target.value})}
                  disabled={!editingInvoice.state}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #cbd5e0",
                    borderRadius: "4px",
                    fontSize: "14px",
                    backgroundColor: !editingInvoice.state ? "#f7fafc" : "white",
                  }}
                >
                  <option value="">Select District</option>
                  {getDistrictsForEditState().map((district, index) => (
                    <option key={index} value={district}>
                      {district}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  VC Name
                </label>
                <select
                  value={editingInvoice.vc_name?.name || editingInvoice.vc_name || ""}
                  onChange={(e) => setEditingInvoice({...editingInvoice, vc_name: e.target.value})}
                  disabled={!editingInvoice.state}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #cbd5e0",
                    borderRadius: "4px",
                    fontSize: "14px",
                    backgroundColor: !editingInvoice.state ? "#f7fafc" : "white",
                  }}
                >
                  <option value="">Select VC</option>
                  {getVCsForEditState().map((vc) => (
                    <option key={vc._id} value={vc.name}>
                      {vc.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  Bill Period
                </label>
                <input
                  type="text"
                  value={editingInvoice.bill_period || ""}
                  onChange={(e) => setEditingInvoice({...editingInvoice, bill_period: e.target.value})}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #cbd5e0",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  Bill Type
                </label>
                <select
                  value={editingInvoice.bill_type || ""}
                  onChange={(e) => setEditingInvoice({...editingInvoice, bill_type: e.target.value})}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #cbd5e0",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                >
                  <option value="">Select Bill Type</option>
                  <option value="regular">Regular</option>
                  <option value="supplementary">Supplementary</option>
                </select>
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  Invoice Amount (₹)
                </label>
                <input
                  type="number"
                  value={editingInvoice.invoice_amount || ""}
                  onChange={(e) => setEditingInvoice({...editingInvoice, invoice_amount: e.target.value})}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #cbd5e0",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  TDS Deducted (₹)
                </label>
                <input
                  type="number"
                  value={editingInvoice.tds || ""}
                  onChange={(e) => setEditingInvoice({...editingInvoice, tds: e.target.value})}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #cbd5e0",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  Amount Deducted (₹)
                </label>
                <input
                  type="number"
                  value={editingInvoice.amount_deducted || ""}
                  onChange={(e) => setEditingInvoice({...editingInvoice, amount_deducted: e.target.value})}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #cbd5e0",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  Payment Received (₹)
                </label>
                <input
                  type="number"
                  value={editingInvoice.payment_received || ""}
                  onChange={(e) => setEditingInvoice({...editingInvoice, payment_received: e.target.value})}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #cbd5e0",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  Received Date
                </label>
                <input
                  type="date"
                  value={editingInvoice.recived_date ? new Date(editingInvoice.recived_date).toISOString().split('T')[0] : ""}
                  onChange={(e) => setEditingInvoice({...editingInvoice, recived_date: e.target.value})}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #cbd5e0",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  Submitted On
                </label>
                <input
                  type="text"
                  value={editingInvoice.submitted_date || ""}
                  onChange={(e) => setEditingInvoice({...editingInvoice, submitted_date: e.target.value})}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #cbd5e0",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  Submitted To
                </label>
                <input
                  type="text"
                  value={editingInvoice.submitted_to || ""}
                  onChange={(e) => setEditingInvoice({...editingInvoice, submitted_to: e.target.value})}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #cbd5e0",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  Contact Number
                </label>
                <input
                  type="text"
                  value={editingInvoice.contact_no || ""}
                  onChange={(e) => setEditingInvoice({...editingInvoice, contact_no: e.target.value})}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #cbd5e0",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  Courier Company
                </label>
                <input
                  type="text"
                  value={editingInvoice.courier_company || ""}
                  onChange={(e) => setEditingInvoice({...editingInvoice, courier_company: e.target.value})}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #cbd5e0",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  Consignment No.
                </label>
                <input
                  type="text"
                  value={editingInvoice.consignment_no || ""}
                  onChange={(e) => setEditingInvoice({...editingInvoice, consignment_no: e.target.value})}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #cbd5e0",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div style={{ gridColumn: isMobile ? "1" : "1 / -1" }}>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  Remarks
                </label>
                <textarea
                  value={editingInvoice.remarks || ""}
                  onChange={(e) => setEditingInvoice({...editingInvoice, remarks: e.target.value})}
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #cbd5e0",
                    borderRadius: "4px",
                    fontSize: "14px",
                    resize: "vertical",
                  }}
                />
              </div>

            </div>

            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", marginTop: "20px" }}>
              <button
                onClick={() => {
                  setEditModalOpen(false);
                  setEditingInvoice(null);
                }}
                style={{
                  padding: "8px 16px",
                  borderRadius: "4px",
                  border: "1px solid #cbd5e0",
                  backgroundColor: "white",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                style={{
                  padding: "8px 16px",
                  borderRadius: "4px",
                  border: "none",
                  backgroundColor: "#3b82f6",
                  color: "white",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmModal.open && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9998,
          backdropFilter: "blur(3px)",
        }}>
          <div style={{
            backgroundColor: "white",
            padding: "30px",
            borderRadius: "12px",
            width: isMobile ? "85%" : "450px",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
            textAlign: "center",
          }}>
            <div style={{ fontSize: "48px", marginBottom: "20px" }}>⚠️</div>
            <h3 style={{ fontSize: "22px", fontWeight: "600", marginBottom: "12px", color: "#1a202c" }}>
              Confirm Delete
            </h3>
            <p style={{ fontSize: "16px", color: "#4a5568", marginBottom: "30px", lineHeight: "1.5" }}>
              Are you sure you want to delete invoice <strong>{deleteConfirmModal.invoiceNo}</strong>?
              <br />
              This action cannot be undone.
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              <button
                onClick={() => setDeleteConfirmModal({ open: false, invoiceNo: null })}
                style={{
                  padding: "10px 24px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e0",
                  backgroundColor: "white",
                  color: "#4a5568",
                  cursor: "pointer",
                  fontSize: "15px",
                  fontWeight: "500",
                  minWidth: "100px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  padding: "10px 24px",
                  borderRadius: "6px",
                  border: "none",
                  backgroundColor: "#e53e3e",
                  color: "white",
                  cursor: "pointer",
                  fontSize: "15px",
                  fontWeight: "500",
                  minWidth: "100px",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {bulkDeleteModal.open && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9998,
          backdropFilter: "blur(4px)",
        }}>
          <div style={{
            backgroundColor: "white",
            padding: "35px",
            borderRadius: "12px",
            width: isMobile ? "90%" : "500px",
            boxShadow: "0 15px 50px rgba(0, 0, 0, 0.4)",
            textAlign: "center",
          }}>
            <div style={{ fontSize: "60px", marginBottom: "20px", color: "#e53e3e" }}>⚠️</div>
            <h3 style={{ fontSize: "24px", fontWeight: "700", marginBottom: "16px", color: "#1a202c" }}>
              Delete Selected Invoices?
            </h3>
            <p style={{ fontSize: "17px", color: "#4a5568", marginBottom: "24px", lineHeight: "1.6" }}>
              You are about to delete <strong>{selectedInvoiceIds.length} selected invoice(s)</strong>.
              <br />
              This action cannot be undone.
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              <button
                onClick={() => setBulkDeleteModal({ open: false, type: '' })}
                style={{
                  padding: "12px 28px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e0",
                  backgroundColor: "white",
                  color: "#4a5568",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "500",
                  minWidth: "120px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkDelete}
                style={{
                  padding: "12px 28px",
                  borderRadius: "6px",
                  border: "none",
                  backgroundColor: "#e53e3e",
                  color: "white",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "600",
                  minWidth: "120px",
                }}
              >
                Delete Selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Loader Overlay */}
      {isUploading && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          backdropFilter: "blur(5px)",
        }}>
          <div style={{
            width: "80px",
            height: "80px",
            border: "8px solid #f3f3f3",
            borderTop: "8px solid #3b82f6",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }} />
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          <p style={{ color: "white", fontSize: "18px", fontWeight: "600", marginTop: "20px" }}>
            Uploading PDF...
          </p>
        </div>
      )}

      {/* CSV Pre-Validation Error Modal */}
      <CsvValidationModal
        open={!!csvValidationErrors}
        onClose={() => setCsvValidationErrors(null)}
        errorData={csvValidationErrors}
      />
    </div>
  );
};

export default InvoiceManagementDashboard;
